import type {
  ColumnSeries,
  FunnelSeriesItem,
  LineSeriesItem,
} from '@spaced-out/ui-design-system/lib/components/Charts';
import {numberToIntlString} from 'src/utils/number';

import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {
  PieSeriesOptions,
  SeriesOptionsType,
} from '@spaced-out/ui-design-system/lib/types/charts';
import type {AlertSemanticType} from '@spaced-out/ui-design-system/lib/types/common';
import {
  addTimezoneEndOfDay,
  addTimezoneStartOfDay,
  formatIsoDate,
  getSubtractedDate,
  getTodayInTimezone,
} from '@spaced-out/ui-design-system/lib/utils';
import lodash from 'lodash';
import moment from 'moment-timezone';
import {filterEntityAttributesForEntityType} from 'src/selectors/ats-entities';
import type {StatusSemanticType} from '@spaced-out/ui-design-system/lib/components/StatusIndicator';
import type {
  Entity,
  FilterByID,
  FilterByKey,
  J2SelectedFilters,
  JobSource,
  JourneyOverviewDataTable,
  JourneyOverviewDataTableQuery,
  JourneyTableListAPIPayload,
  JourneyTouchpointBlock,
  KPIHiringMetric,
  MetricData,
  MetricDataObject,
  MultilevelFilterState,
  Node,
  NPSOverviewDataTable,
  SelectedFilters,
  SelectedQuestionFilters,
  SurveyNPSActiveTabs,
  TreeNode,
  TreeNodeKey,
  WorkflowOverviewDataTable,
  WorkflowOverviewDataTableQuery,
  WorkflowOverviewTableRowData,
  WorkflowTableListAPIPayload,
  SurveyV2ActiveTabs,
  SurveyV2AllResponsesResponse,
} from 'src/types/analytics-genesis';
import type {EntityAttributeSchema, EntityType} from 'src/types/ats-entities';
import type {
  MakeKeyFunction,
  TranslationFunction,
} from 'src/types/language-support';


export const DATE_RANGES = {
  sevenDays: 7,
  fifteenDays: 15,
  thirtyDays: 30,
  sixtyDays: 60,
  ninetyDays: 90,
} as const;

// Survey V2 Tabs ̰
export const getSurveyV2Tabs = (t: TranslationFunction): SurveyV2ActiveTabs => [
  // {
  //   id: 'all-surveys',
  //   label: t('All_Surveys', 'All Surveys'),
  // },
  {
    id: 'all-responses',
    label: t('All_Responses', 'All Responses'),
  },
];

// Survey V2 KPI Metrics Order
export const SurveyV2KpiMetricsOrder = [
  'surveys_sent',
  'surveys_started',
  'surveys_completed',
  'completion_time',
] as const;

// Survey V2 Table Columns Query
export const surveyV2TableColumnsQuery = {
  product: 'surveys-v2-dashboard',
  dashboard: 'surveys-v2-dashboard',
  table: 'all-surveys',
};

// Survey V2 All Responses Table Columns Query
export const surveyV2AllResponsesTableColumnsQuery = {
  product: 'surveys-v2',
  dashboard: 'surveys-v2',
  sub_dashboard: 'all-responses',
  table: 'all-responses-table',
};

// Export Sources
export const EXPORT_SURVEY_V2_SOURCE = 'surveys-v2-all-surveys';
export const EXPORT_SURVEY_V2_ALL_RESPONSES =
  'data-exports/all-surveys-v2/all-responses-table';
export const EXPORT_SURVEY_V2_ALL_RESPONSES_MCQ =
  'data-exports/all-surveys-v2/all-responses-table-mcq';

// Menu Options for Survey V2 Table
export const getMenuOptionsForSurveyV2Table = (t: TranslationFunction) => [
  {
    key: 'survey_name',
    label: t('Survey_Name', 'Survey Name'),
  },
  {
    key: 'surveys_sent',
    label: t('Surveys_Sent', 'Surveys Sent'),
  },
  {
    key: 'surveys_started',
    label: t('Surveys_Started', 'Surveys Started'),
  },
  {
    key: 'surveys_completed',
    label: t('Surveys_Completed', 'Surveys Completed'),
  },
  {
    key: 'completion_rate',
    label: t('Completion_Rate', 'Completion Rate'),
  },
  {
    key: 'completion_time',
    label: t('Completion_Time', 'Completion Time'),
  },
  {
    key: 'entityVariable',
    label: t('Entity_Variable', 'Entity Variable'),
    disabled: true,
  },
];

// Menu Options for Survey V2 All Responses Table
export const getMenuOptionsForSurveyV2AllResponsesTable = (
  t: TranslationFunction,
) => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {key: 'survey_name', label: t('Survey_Name', 'Survey Name')},
  {key: 'workflow_name', label: t('Workflow_Name', 'Workflow Name')},
  {key: 'node_name', label: t('Node_Name', 'Node Name')},
  {key: 'question_text', label: t('Question_Text', 'Question Text')},
  {key: 'question_type', label: t('Question_Type', 'Question Type')},
  {
    key: 'question_response',
    label: t('Question_Response', 'Question Response'),
  },
  {key: 'email', label: t('Email', 'Email')},
  {key: 'phone_number', label: t('Phone_Number', 'Phone Number')},
  {key: 'focus_entity', label: t('Entity_Type', 'Entity Type')},
  {
    key: 'completion_status',
    label: t('Completion_Status', 'Completion Status'),
  },
  {key: 'node_id', label: t('Node_ID', 'Node ID')},
  {key: 'workflow_id', label: t('Workflow_ID', 'Workflow ID')},
  {key: 'survey_id', label: t('Survey_ID', 'Survey ID')},
  {key: 'name', label: t('Name', 'Name')},
  {key: 'recipient_type', label: t('Recipient_Type', 'Recipient Type')},
  {key: 'channel', label: t('Channel', 'Channel')},
  {key: 'completed_on', label: t('Completed_On', 'Completed On')},
  {key: 'delivered_on', label: t('Delivered_On', 'Delivered On')},
  {key: 'opened_on', label: t('Opened_On', 'Opened On')},
  {key: 'sent_on', label: t('Sent_On', 'Sent On')},
  {key: 'started_on', label: t('Started_On', 'Started On')},
  {key: 'time_clicked', label: t('Time_Clicked', 'Time Clicked')},
];

// Survey V2 Export Options
export const getSurveyV2ExportOptions = (t: TranslationFunction) => [
  {
    key: 'all-responses',
    label: t('All_Responses', 'All Responses'),
  },
  {
    key: 'mcq-summary',
    label: t('MCQ_Summary', 'MCQ Summary'),
  },
];

export const METRICS_COUNT_BY_VIEW = {
  overview: 3,
  email: 6,
  sms: 6,
} as const;

export const JOURNEY_PAGE_SIZE = 10;
export const DEBOUNCE_DELAY = 500;
export const PAGE_SIZE_TYPEAHEAD = 15;

export const KPIMetricsOrder = [
  'people_reached',
  'unsubscribed',
  'spam',
  'sent',
  'delivered',
  'opened',
  'clicked',
  'skipped',
  'bounced',
  'replies',
] as const;

// Voice Call Analytics Utilities
export const VoiceCallKpiMetricsOrder = [
  'calls_attempted',
  'calls_answered',
  'calls_dropped',
  'calls_completed',
  'calls_rescheduled',
  'avg_calls_duration',
  'median_calls_duration',
] as const;

export const getVoiceCallTabs = (t: TranslationFunction) => [
  {
    id: 'overview',
    label: t('Overview', 'Overview'),
  },
  {
    id: 'all-voiceflows',
    label: t('All_Voiceflows', 'All Voiceflows'),
  },
  {
    id: 'all-workflows',
    label: t('All_Workflows', 'All Workflows'),
  },
  {
    id: 'all-call-records',
    label: t('All_Call_Records', 'All Call Records'),
  },
];

export const getVoiceCallMetricDisplayName = (
  metricKey: string,
  t: TranslationFunction,
) => {
  const metricNames: Record<string, string> = {
    calls_attempted: t('Calls_Attempted', 'Calls Attempted'),
    calls_answered: t('Calls_Answered', 'Calls Answered'),
    calls_dropped: t('Calls_Dropped', 'Calls Dropped'),
    calls_completed: t('Calls_Completed', 'Calls Completed'),
    calls_rescheduled: t('Calls_Rescheduled', 'Calls Rescheduled'),
    avg_calls_duration: t('Avg_Call_Duration', 'Avg Call Duration'),
    median_calls_duration: t('Median_Call_Duration', 'Median Call Duration'),
  };
  return metricNames[metricKey] || metricKey;
};

export const getIconNameForVoiceCallMetrics = (metricKey: string) => {
  const iconNames: Record<string, string> = {
    calls_attempted: 'phone-arrow-up-right',
    calls_answered: 'phone-volume',
    calls_dropped: 'phone-xmark',
    calls_completed: 'check-circle',
    calls_rescheduled: 'calendar',
    avg_calls_duration: 'clock',
    median_calls_duration: 'clock',
  };

  const iconSemantics: Record<string, AlertSemanticType> = {
    calls_attempted: 'neutral',
    calls_answered: 'neutral',
    calls_dropped: 'danger',
    calls_completed: 'success',
    calls_rescheduled: 'neutral',
    avg_calls_duration: 'neutral',
    median_calls_duration: 'neutral',
  };
  return {
    iconName: iconNames[metricKey] || 'info-circle',
    iconSemantic: iconSemantics[metricKey] || 'neutral',
    iconType: 'solid',
  };
};

export const getBooleanMenuOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'true',
    label: t('True', 'True'),
  },
  {
    key: 'false',
    label: t('False', 'False'),
  },
];

export const timeRangeOptions = [
  {
    key: 'thisYear',
    label: 'This year',
  },
  {
    key: 'sevenDays',
    label: 'Last 7 Days',
  },
  {
    key: 'thirtyDays',
    label: 'Last 30 days',
  },
  {
    key: 'sixtyDays',
    label: 'Last 60 days',
  },
  {
    key: 'ninetyDays',
    label: 'Last 90 days',
  },
  {
    key: 'lastYear',
    label: 'Last year',
  },
  {
    key: 'custom',
    label: 'Custom time',
  },
] as const;

export const getTimeRangeOptions = (
  currentYear: number,
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => {
  return [
    {
      key: 'thisYear',
      label: `${t('Current_Year', 'Current Year')} (${t(
        'YTD',
        'YTD',
      )} ${currentYear})`,
    },
    {
      key: 'sevenDays',
      label: t('Last_7_Days', 'Last 7 Days'),
    },
    {
      key: 'thirtyDays',
      label: t('Last_30_days', 'Last 30 days'),
    },
    {
      key: 'sixtyDays',
      label: t('Last_60_days', 'Last 60 days'),
    },
    {
      key: 'ninetyDays',
      label: t('Last_90_days', 'Last 90 days'),
    },
    {
      key: 'lastYear',
      label: `${t('Previous_Year', 'Previous Year')} (${currentYear - 1})`,
    },
    {
      key: 'custom',
      label: t('Custom_Time', 'Custom Time'),
    },
  ];
};

export const NoRecordsOptions = [
  {key: 'noRecords', label: 'No Records Found'},
] as const;

export const journeyTableColumnsQuery = {
  product: 'journeys',
  dashboard: 'engagement-and-deliverability',
  sub_dashboard: 'journey-view',
  table: 'journeys-data-table',
} as const;

export const touchpointTableColumnsQuery = {
  product: 'journeys',
  dashboard: 'engagement-and-deliverability',
  sub_dashboard: 'touchpoint-view',
  table: 'touchpoints-data-table',
} as const;

export const peopleTableColumnsQuery = {
  product: 'journeys',
  dashboard: 'engagement-and-deliverability',
  sub_dashboard: 'people-view',
  table: 'peoples-data-table',
} as const;

export const getTimePeriodsOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'quarter', label: t('Quarterly', 'Quarterly')},
  {key: 'month', label: t('Monthly', 'Monthly')},
  {key: 'week', label: t('Weekly', 'Weekly')},
];

export const roiSummaryMetrics = [
  {label: 'reached', icon: 'radar'},
  {label: 'sourced', icon: 'filter'},
  {label: 'submitted', icon: 'marker'},
  {label: 'hired', icon: 'trophy-star'},
] as const;

export const getHiringMetricsCategories = (
  t: (arg1: string, arg2: string) => string,
): Array<KPIHiringMetric> => [
  {
    key: 'time_to_hire',
    label: t('Time_to_Hire', 'Time to Hire'),
    category: 'Hiring Velocity',
    categoryLabel: t('Hiring_Velocity', 'Hiring Velocity'),
    icon: 'rabbit-running',
  },
  {
    key: 'time_to_submit',
    label: t('Time_to_Submit', 'Time to Submit'),
    category: 'Hiring Velocity',
    categoryLabel: t('Hiring_Velocity', 'Hiring Velocity'),
    icon: 'rabbit-running',
  },

  {
    key: 'hours_saved',
    label: t('FTE_Hours_Saved', 'FTE Hours Saved'),
    category: 'Savings',
    categoryLabel: t('Savings', 'Savings'),
    icon: 'piggy-bank',
  },
  {
    key: 'spend_saved',
    label: t('Job_Board_Spend_Saved', 'Job Board Spend Saved'),
    category: 'Savings',
    categoryLabel: t('Savings', 'Savings'),
    icon: 'piggy-bank',
  },

  {
    key: 'influenced_revenue',
    label: t('Influenced_Gross_Revenue', 'Influenced Gross Revenue'),
    category: 'Revenue',
    categoryLabel: t('Revenue', 'Revenue'),
    icon: 'circle-dollar',
  },
  {
    key: 'influenced_margin',
    label: t('Influenced_Gross_Margin', 'Influenced Gross Margin'),
    category: 'Revenue',
    categoryLabel: t('Revenue', 'Revenue'),
    icon: 'circle-dollar',
  },
];

export const getRatingByScore = (
  score: number,
  t: (arg1: string, arg2: string) => string,
): {
  rating: number;
  label: string;
} => {
  const ratingData = [
    {maxScore: 40, rating: 1, label: t('Not_Qualified', 'Not Qualified')},
    {maxScore: 55, rating: 2, label: t('Poor_Match', 'Poor Match')},
    {
      maxScore: 70,
      rating: 3,
      label: t('Satisfactory_Match', 'Satisfactory Match'),
    },
    {maxScore: 90, rating: 4, label: t('Good_Match', 'Good Match')},
    {
      maxScore: Infinity,
      rating: 5,
      label: t('Excellent_Match', 'Excellent Match'),
    },
  ];

  if (!score) {
    return {rating: 0, label: ''};
  }

  const result = ratingData.find(({maxScore}) => score < maxScore) || {};
  // @ts-expect-error - TS2339 - Property 'rating' does not exist on type '{}'. | TS2339 - Property 'label' does not exist on type '{}'.
  return {rating: result.rating || 0, label: result.label || ''};
};

export const getExportOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'engage',
    label: t('Journey_Performance', 'Journey Performance'),
  },
  {
    key: 'messaging',
    label: t('Messaging_Performance', 'Messaging Performance'),
  },
  {
    key: 'inbound_chatbot',
    label: t('Inbound_Chatbot_Performance', 'Inbound Chatbot Performance'),
  },
  {
    key: 'referral',
    label: t('Referral_Performance', 'Referral Performance'),
  },
];

export const getChannels = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'all', label: t('All', 'All')},
  {key: 'email', label: t('Email', 'Email')},
  {key: 'sms', label: t('SMS', 'SMS')},
];

export const getTypeOfMetric = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'absolute_numbers', label: t('Absolute_Numbers', 'Absolute Numbers')},
  {key: 'conversion_rates', label: t('Conversion_Rates', 'Conversion Rates')},
];

export const getReportsChartTimePeriodOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'quarterly', label: t('Quarterly', 'Quarterly')},
  {key: 'monthly', label: t('Monthly', 'Monthly')},
  {key: 'weekly', label: t('Weekly', 'Weekly')},
  {key: 'daily', label: t('Daily', 'Daily')},
];

export const getShowAllOption = (
  t: (arg1: string, arg2: string) => string,
): MenuOption => ({
  iconLeft: 'grid-horizontal',
  iconLeftType: 'solid',
  label: t('Show_All', 'Show All'),
  key: 'showAll',
});

export const getDashboardTableList = ({
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'JourneysListState': query, sort, pageNumber
  listState = {},
  pageSize,
  journeyId,
  touchpointId,
  commonPayload,
}: JourneyTableListAPIPayload): JourneyOverviewDataTableQuery => {
  const {sort: {sortBy, sortOrder} = {}, pageNumber, query} = listState;
  let apiQuery = {
    ...commonPayload,
    limit: pageSize,
    offset: (pageNumber - 1) * pageSize,
  };
  if (journeyId) {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'workflow_id' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'.
    apiQuery = {...apiQuery, workflow_id: journeyId};
  }
  if (touchpointId) {
    // @ts-expect-error - TS2561 - Object literal may only specify known properties, but 'event_id' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'. Did you mean to write 'event_ids'?
    apiQuery = {...apiQuery, event_id: touchpointId};
  }
  if (query) {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'query' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'.
    apiQuery = {...apiQuery, query};
  }
  if (sortBy && sortOrder !== 'original') {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sort' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'.
    apiQuery = {...apiQuery, sort: sortOrder === 'asc' ? sortBy : `-${sortBy}`};
  }
  return apiQuery;
};

export const getAttributeOptions = (
  entityType: EntityType,
  attributes: Map<string, EntityAttributeSchema>,
): Array<MenuOption> => {
  if (entityType) {
    const attributesList = Array.from(
      // @ts-expect-error - TS2769 - No overload matches this call.
      filterEntityAttributesForEntityType(attributes, entityType),
    );
    const menuOptions = attributesList
      .map((attribute) => ({
        // @ts-expect-error - TS18046 - 'attribute' is of type 'unknown'.
        key: attribute.id,
        // @ts-expect-error - TS18046 - 'attribute' is of type 'unknown'.
        label: attribute.display_name,
      }))
      .filter((attribute) => Boolean(attribute.key));
    return menuOptions ?? [];
  } else {
    return [];
  }
};

export const getFilterItemOptionsByKey = (
  filterItemList: Array<FilterByKey>,
  t?: (arg1: string, arg2: string) => string,
  makeKey?: (arg1: string) => string,
  isDynamicField: boolean = false,
): Array<MenuOption> => {
  const options = filterItemList.map(({key, value}) => ({
    key,
    label:
      isDynamicField && t ? t(makeKey ? makeKey(value) : value, value) : value,
  }));
  return options;
};

export const getFilterItemOptionsByID = (
  filterItemList: Array<FilterByID>,
): Array<MenuOption> => {
  const options = filterItemList.map(({id, name}) => ({
    key: id,
    label: name,
  }));
  return options;
};

export const getPlaceholderText = (
  options: Array<MenuOption>,
  selectedOptions: Array<string>,
  title: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  const isAllSelected =
    lodash.difference(
      options.map((option) => option.key),
      selectedOptions,
    ).length === 0;

  return isAllSelected
    ? `${t('All', 'All')} ${title}`
    : selectedOptions
        .map((opt) => options.find((item) => item.key === opt)?.label ?? '')
        .join(', ');
};

export const getOverviewDataDisplayName = (
  str: string,
  t: (arg1: string, arg2: string) => string,
  makeKey: (arg1: string) => string,
): string => {
  if (str === 'replies_rate') {
    return t('Reply_Rate', 'Reply Rate');
  }
  if (str === 'avg_time_to_fill') {
    return t('Avg_Time_to_Fill_in_days', 'Avg Time to Fill (in days)');
  }
  if (str === 'referrals') {
    return t('Referrals_in_days', 'Referrals (in days)');
  }
  if (str === 'without_referrals') {
    return t('Without_Referrals_in_days', 'Without Referrals (in days)');
  }
  if (!str.includes('_')) {
    const capitalizedStr = str.charAt(0).toUpperCase() + str.slice(1);
    return t(makeKey(capitalizedStr), capitalizedStr);
  }
  const words = str.split('_');
  switch (words[0]) {
    case 'opened': {
      words[0] = 'open';
      break;
    }
    case 'bounced': {
      words[0] = 'bounce';
      break;
    }
    case 'clicked': {
      words[0] = 'click';
      break;
    }
    case 'delivered': {
      words[0] = 'delivery';
      break;
    }
    case 'unsubscribed': {
      words[0] = 'unsubscribe';
      break;
    }
  }
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  return t(makeKey(words.join(' ')), words.join(' '));
};

export const getJourneyClickOverviewDataDisplayName = (
  str: string,
  t: (arg1: string, arg2: string) => string,
  sentLinkId?: string,
): string => {
  const words = str.split('_');
  switch (words[0]) {
    case 'sent': {
      words[0] = t('Links_Sent', 'Links Sent');
      if (sentLinkId) {
        words[0] = t('Links_Sent_To', 'Links Sent To');
      }
      break;
    }
    case 'clicked': {
      words[0] = t('Links_Clicked', 'Links Clicked');
      if (sentLinkId) {
        words[0] = t('No_of_People_Clicked', 'No of People Clicked');
      }
      break;
    }
  }
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  return words.join(' ');
};

export const getNpsSurveyDataDisplayName = (
  str: string,
  t: TranslationFunction,
  makeKey: MakeKeyFunction,
): string => {
  switch (str) {
    case 'score': {
      return t('NPS_Score', 'NPS Score');
    }
    case 'surveys_started_rate': {
      return t('Start_Rate', 'Start Rate');
    }
    case 'surveys_completed_rate': {
      return t('Completion_Rate', 'Completion Rate');
    }
  }

  const words = str.split('_');
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  return t(makeKey(words.join(' ')), words.join(' '));
};

export const RecruiterMetricsOrder = [
  'total_recruiters',
  'unassigned_recruiters',
  'never_logged_in',
] as const;

export const PipelineVelocityMetricsOrder = [
  'applicants',
  'offered',
  'offer_accepted',
  'hired',
  'joined',
  'time_to_hire',
  'time_to_submit',
  'slowest_milestone',
] as const;
export const PipelineVelocityDrilldownMetricsOrder = [
  'applicants',
  'offered',
  'offer_accepted',
  'hired',
  'joined',
  'time_to_hire',
  'time_to_submit',
  'slowest_milestone',
] as const;
export const JobStatusAgeMetricsOrder = [
  'jobs_created',
  'jobs_open',
  'jobs_onhold',
  'jobs_closed',
] as const;

export const InterviewScheduleMetricsOrder = [
  'interviews_scheduled',
  'interview_review_completed',
  'review_delay',
] as const;

export const GetInterviewScheduleMetricTopValue = (
  metricKey: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metricKey) {
    case 'interviews_scheduled': {
      return t('Interview_Scheduled', 'Interview Scheduled');
    }

    case 'interview_review_completed': {
      return t('Interview_Review_Completed', 'Interview Review Completed');
    }
    case 'review_delay': {
      return t('Average_Review_Delay', 'Average Review Delay');
    }
    default: {
      return '';
    }
  }
};

export const GetJobStatusAgeMetricTopValue = (
  metricKey: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metricKey) {
    case 'jobs_created': {
      return t('Job_Created', 'Job Created');
    }
    case 'jobs_open': {
      return t('Open_Jobs', 'Open Jobs');
    }
    case 'jobs_onhold': {
      return t('Jobs_on_Hold', 'Jobs on Hold');
    }
    case 'jobs_closed': {
      return t('Closed_Jobs', 'Closed Jobs');
    }
    default: {
      return '';
    }
  }
};

export const GetPipelineVelocityMetricTopLabel = (
  metricKey: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metricKey) {
    case 'applicants': {
      return t('Applicants', 'Applicants');
    }
    case 'offered': {
      return t('Offer_Generated', 'Offer Generated');
    }
    case 'offer_accepted': {
      return t('Offer_Accepted', 'Offer Accepted');
    }
    case 'hired': {
      return t('Hired', 'Hired');
    }
    case 'joined': {
      return t('Joined', 'Joined');
    }
    case 'time_to_hire': {
      return t('Time_To_Hire', 'Time To Hire');
    }
    case 'time_to_submit': {
      return t('Time_To_Submit', 'Time To Submit');
    }
    case 'slowest_milestone': {
      return t('Slowest_Milestone', 'Slowest Milestone');
    }
    default: {
      return '';
    }
  }
};

export const GetPipelineVelocityMetricBottomLabel = (
  metricKey: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metricKey) {
    case 'offered_ratio': {
      return t('conversion_from_applicants', 'conversion from applicants');
    }
    case 'offer_accepted_ratio': {
      return t('conversion_from_offered', 'conversion from offered');
    }
    default: {
      return '';
    }
  }
};

export const getConversionSummaryOverviewContent = (
  str: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (str) {
    case 'surveys_sent': {
      return t('Surveys_Sent', 'Surveys Sent');
    }
    case 'surveys_started': {
      return t('Surveys_Started', 'Surveys Started');
    }
    case 'surveys_partially_completed': {
      return t('Surveys_Partially_Completed', 'Surveys Partially Completed');
    }
    case 'surveys_started_rate': {
      return t('Start_Rate', 'Start Rate');
    }
    case 'surveys_completed_rate': {
      return t('Completion_Rate', 'Completion Rate');
    }
    case 'surveys_completed': {
      return t('Surveys_Completed', 'Surveys Completed');
    }
    default:
      return '';
  }
};

export const getEngagementKpiMetricsDisplayName = (
  str: string,
  t: TranslationFunction,
): string => {
  switch (str) {
    case 'sent': {
      return t('Sent', 'Sent');
    }
    case 'delivered': {
      return t('Delivered', 'Delivered');
    }
    case 'delivered_percentage': {
      return t('Delivery_Rate', 'Delivery Rate');
    }
    case 'opened': {
      return t('Opened_Only_Emails', 'Opened (Only Emails)');
    }
    case 'opened_percentage': {
      return t('Open_Rate', 'Open Rate');
    }
    case 'skipped': {
      return t('Skipped', 'Skipped');
    }
    case 'skipped_percentage': {
      return t('Skip_Rate', 'Skip Rate');
    }
    case 'bounced': {
      return t('Bounced', 'Bounced');
    }
    case 'bounced_percentage': {
      return t('Bounce_Rate', 'Bounce Rate');
    }
    case 'replies': {
      return t('Responded', 'Responded');
    }
    case 'replies_percentage': {
      return t('Response_Rate', 'Response Rate');
    }
    case 'unsubscribed': {
      return t('Unsubscribed', 'Unsubscribed');
    }
    case 'unsubscribed_percentage': {
      return t('Unsubscribe_Rate', 'Unsubscribe Rate');
    }
    case 'spam': {
      return t('Spam_Only_Emails', 'Spam (Only Emails)');
    }
    case 'spam_percentage': {
      return t('Spam_Rate', 'Spam Rate');
    }
    default: {
      return t('NA', 'N/A');
    }
  }
};

export const getDateRangeDataByOption = (
  selectedDateRangeOption: string,
  timezone: string = '',
): DateRangeWithTimezone => {
  const today = getTodayInTimezone(timezone);
  let rangeStartDate, rangeEndDate;

  switch (selectedDateRangeOption) {
    case 'thisYear':
      // @ts-expect-error - TS2345 - Argument of type 'Moment' is not assignable to parameter of type 'string | Date | undefined'.
      rangeStartDate = formatIsoDate(moment.utc(today).startOf('y'));
      rangeEndDate = today;
      break;
    case 'lastYear':
      const previousYear = moment.utc(today).subtract(1, 'y').year();
      rangeStartDate = `${previousYear}-01-01`;
      rangeEndDate = `${previousYear}-12-31`;
      break;
    default:
      rangeStartDate = getSubtractedDate(
        today,
        //  Vivek: Subtracting -1 from here as we want to include the current day in the range
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly sevenDays: 7; readonly fifteenDays: 15; readonly thirtyDays: 30; readonly sixtyDays: 60; readonly ninetyDays: 90; }'.
        DATE_RANGES[selectedDateRangeOption] - 1,
        'd',
      );
      rangeEndDate = today;
      break;
  }

  // Adding timezone to dates
  const rangeStartDateUTC = addTimezoneStartOfDay(rangeStartDate, timezone);
  const rangeEndDateUTC = addTimezoneEndOfDay(rangeEndDate, timezone);

  return {
    startDateUTC: rangeStartDateUTC,
    endDateUTC: rangeEndDateUTC,
    startDate: rangeStartDate,
    endDate: rangeEndDate,
    timezone,
  };
};

export const getOptionByDataRange = (
  dateRange: DateRangeWithTimezone,
  timezone?: string,
  // @ts-expect-error - TS4104 - The type 'readonly [{ readonly key: "thisYear"; readonly label: "This year"; }, { readonly key: "sevenDays"; readonly label: "Last 7 Days"; }, { readonly key: "thirtyDays"; readonly label: "Last 30 days"; }, { ...; }, { ...; }, { ...; }, { ...; }]' is 'readonly' and cannot be assigned to the mutable type 'MenuOption[]'.
  rangeOptions: Array<MenuOption> = timeRangeOptions,
): MenuOption => {
  let option;
  rangeOptions.forEach((menuOption) => {
    const {key} = menuOption;
    const {startDate, endDate} = getDateRangeDataByOption(key, timezone);
    if (startDate === dateRange.startDate && endDate === dateRange.endDate) {
      option = menuOption;
    }
  });
  return option ?? rangeOptions.slice(-1)[0];
};

export const formatDateText = (
  date?: string,
  format: string = 'MM.DD.YYYY',
  locale?: string,
): string => {
  if (locale) {
    moment.locale(locale);
  }
  return date ? moment.utc(date).format(format) : '';
};

export const getTimePeriodOption = (
  startDate: string,
  endDate: string,
  t: (arg1: string, arg2: string) => string,
  isJourneyDashboard: boolean = false,
): MenuOption => {
  const startDateMoment = moment.utc(startDate);
  const endDateMoment = moment.utc(endDate);
  const daysDiff = Math.abs(endDateMoment.diff(startDateMoment, 'd'));

  const getExactKey = (range: string) =>
    isJourneyDashboard ? `${range}ly` : range;

  if (daysDiff <= 7 && isJourneyDashboard) {
    return {key: 'daily', label: t('Daily', 'Daily')};
  } else if (daysDiff <= 30) {
    return {key: getExactKey('week'), label: t('Weekly', 'Weekly')};
  } else if (daysDiff <= 180) {
    return {key: getExactKey('month'), label: t('Monthly', 'Monthly')};
  } else {
    return {
      key: getExactKey('quarter'),
      label: t('Quarterly', 'Quarterly'),
    };
  }
};

export const getSymbolFromCurrencyCode = (currencyCode: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  });
  const formatted = formatter.format(0);
  const symbol = formatted.replace(/[\d.,\s]/g, '');

  return symbol;
};

export const getInitialFrequency = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  t: (arg1: string, arg2: string) => string,
): string => {
  if (startDate && endDate) {
    return getTimePeriodOption(startDate, endDate, t, true).key;
  } else {
    return 'quarterly';
  }
};

export const getValidFrequencyOptions = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => {
  const reportsChartTimePeriodOptions = getReportsChartTimePeriodOptions(t);
  const frequencyIndex = lodash.findIndex(reportsChartTimePeriodOptions, {
    key: getInitialFrequency(startDate, endDate, t),
  });
  return reportsChartTimePeriodOptions.filter(
    (_, index) => index >= frequencyIndex,
  );
};

export const getValidTimePeriodOptions = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => {
  let key = 'quarterly';
  if (startDate && endDate) {
    key = getTimePeriodOption(startDate, endDate, t).key;
  }
  const timePeriodsOptions = getTimePeriodsOptions(t);
  const timePeriodIndex = lodash.findIndex(timePeriodsOptions, {key});
  return timePeriodsOptions.filter((_, index) => index >= timePeriodIndex);
};

export const transformEntities = (
  entities: Array<Entity>,
): {
  and: unknown;
} => {
  // @ts-expect-error - TS7034 - Variable 'transformedEntities' implicitly has type 'any[]' in some locations where its type cannot be determined.
  const transformedEntities = [];
  entities.forEach(({entityType, attribute, attributeValue}) => {
    if (
      entityType &&
      attribute &&
      !lodash.isNil(attributeValue) &&
      !lodash.isEmpty(attributeValue)
    ) {
      let newEntity;
      const field = `${entityType}/${attribute.attribute_name}`;
      if (lodash.isArray(attributeValue)) {
        newEntity = {
          in: {
            field,
            value: attributeValue,
          },
        };
      } else if (
        lodash.isObject(attributeValue) &&
        attributeValue?.min &&
        attributeValue.max
      ) {
        const {max, min} = attributeValue;
        newEntity = {
          and: [
            {
              lte: {
                field,
                value: max,
              },
            },
            {
              gte: {
                field,
                value: min,
              },
            },
          ],
        };
      } else {
        newEntity = {
          eq: {
            field,
            value: attributeValue,
          },
        };
      }
      transformedEntities.push(newEntity);
    }
  });

  // @ts-expect-error - TS7005 - Variable 'transformedEntities' implicitly has an 'any[]' type.
  return {and: transformedEntities};
};

export const transformJourneyTouchpoints = (
  journeyTouchpoints: Array<JourneyTouchpointBlock>,
): Array<Omit<JourneyTouchpointBlock, 'id'>> => {
  return journeyTouchpoints.map(
    ({journeyId, journeyName, touchpointIds, touchpointNames}) => ({
      journeyId,
      journeyName,
      touchpointIds:
        touchpointIds && Array.isArray(touchpointIds)
          ? [...touchpointIds].sort()
          : touchpointIds,
      touchpointNames:
        touchpointNames && Array.isArray(touchpointNames)
          ? [...touchpointNames].sort((a, b) => a.id.localeCompare(b.id))
          : touchpointNames,
    }),
  );
};

export const transformQuestion = (
  questions: Array<SelectedQuestionFilters>,
): Array<SelectedQuestionFilters> => {
  const transformedQuestions: Array<SelectedQuestionFilters> = [];

  questions.forEach(({question_id, response_type}) => {
    if (question_id && response_type) {
      transformedQuestions.push({question_id, response_type});
    }
  });

  return transformedQuestions;
};

export const itemsOnPageText = (
  pageNumber: number | null | undefined = 1,
  pageSize: number,
  totalCount: number,
  t: (arg1: string, arg2: string) => string,
  convertToIntlString?: boolean,
): string => {
  let pageStart = 0;
  let pageEnd = 0;

  if (totalCount > 0) {
    // @ts-expect-error - TS18047 - 'pageNumber' is possibly 'null'.
    pageStart = (pageNumber - 1) * pageSize + 1;
    // @ts-expect-error - TS18047 - 'pageNumber' is possibly 'null'.
    pageEnd = Math.min(pageNumber * pageSize, totalCount);
  }

  return (
    t('Items_on_page', 'Items on page') +
    `: ${convertToIntlString ? numberToIntlString(pageStart) : pageStart} - ${
      convertToIntlString ? numberToIntlString(pageEnd) : pageEnd
    } ${t('of', 'of')} ${
      convertToIntlString ? numberToIntlString(totalCount) : totalCount
    }`
  );
};

export const transformTableData = (
  input: JourneyOverviewDataTable | null,
): JourneyOverviewDataTable => {
  const {data = [], total_count = 0} = lodash.cloneDeep(input) ?? {};
  const entityKeys: Set<string> = new Set();

  const transformedData =
    data.map((row) => {
      // Collect all keys from the row itself

      if (row.entity_attributes) {
        // Collect all keys from entity_attributes
        Object.keys(row.entity_attributes).forEach((key) =>
          entityKeys.add(key),
        );

        // Merge entity_attributes into the main row
        Object.assign(row, row.entity_attributes);

        // Remove the entity_attributes property
        delete row.entity_attributes;
      }

      return row;
    }) || [];

  const allAttributesColumn = Array.from(entityKeys);

  // Return the transformed table data
  return {
    ...input,
    data: transformedData,
    allAttributesColumn,
    total_count,
  };
};

export const transformWorkflowTableData = (
  input: WorkflowOverviewDataTable,
): WorkflowOverviewDataTable => {
  const entityKeys: Set<string> = new Set();

  const transformedData =
    input.data?.map((row: WorkflowOverviewTableRowData) => {
      // Collect all keys from the row itself

      if (row.entity_attributes) {
        // Collect all keys from entity_attributes
        Object.keys(row.entity_attributes).forEach((key) =>
          entityKeys.add(key),
        );

        // Merge entity_attributes into the main row
        Object.assign(row, row.entity_attributes);

        // Remove the entity_attributes property
        delete row.entity_attributes;
      }

      return row;
    }) || [];

  // Convert the sets to arrays

  const allAttributesColumn = Array.from(entityKeys);

  // Return the transformed table data
  return {
    ...input,
    data: transformedData,
    allAttributesColumn,
  };
};

export const convertStringCase = (str: string): string => {
  // Replace underscores with spaces and capitalize the first letter of each word
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const removeLockedFilters = (
  payload: SelectedFilters,
  journey: string,
  touchpoint: string,
): SelectedFilters => {
  if (journey) {
    // the following filters are locked when a journey is selected
    const newPayload = lodash.omit(payload, [
      'journey_lifecycle_goals',
      'journey_business_goals',
      'journey_categories',
      'recipient_type',
      'journey_status',
      'journey_ids',
    ]);
    if (touchpoint) {
      // touchpoint type is locked when a touchpoint is selected
      return {
        ...lodash.omit(newPayload, ['event_types']),
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'workflow_id' does not exist in type 'SelectedFilters'.
        workflow_id: journey,
        event_id: touchpoint,
      };
    } else {
      // delivery status filter isn't present when a touchpoint isn't selected
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'workflow_id' does not exist in type 'SelectedFilters'.
      return {...newPayload, workflow_id: journey};
    }
  } else {
    return lodash.omit(payload, ['event_types']);
  }
};

export const removeLockedFiltersJ2 = (
  payload: J2SelectedFilters,
  workflow: string,
  node: string,
): J2SelectedFilters => {
  if (workflow) {
    // the following filters are locked when a workflow is selected
    let newPayload = lodash.omit(payload, [
      'workflow_categories',
      'workflow_status',
      'recipient_type',
      'workflow_ids',
    ]);
    if (node) {
      // node type is locked when a node is selected
      newPayload = lodash.omit(newPayload, ['node_types']);
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'workflow_id' does not exist in type 'Omit<J2SelectedFilters, "workflow_ids" | "recipient_type" | "workflow_status" | "workflow_categories">'.
      newPayload = {...newPayload, workflow_id: workflow, node_id: node};
    } else {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'workflow_id' does not exist in type 'Omit<J2SelectedFilters, "workflow_ids" | "recipient_type" | "workflow_status" | "workflow_categories">'.
      newPayload = {...newPayload, workflow_id: workflow};
    }
    return newPayload;
  } else {
    return lodash.omit(payload, ['node_types']);
  }
};

export const removeLockedJourneyClickInsightsFilters = (
  payload: SelectedFilters,
  sentLinkId?: string,
): SelectedFilters => {
  if (sentLinkId) {
    // the following filters are locked when a link is selected
    const newPayload = lodash.omit(payload, [
      'journey_lifecycle_goals',
      'journey_business_goals',
      'journey_categories',
      'recipient_type',
      'journey_status',
      'journey_ids',
    ]);
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sent_link_id' does not exist in type 'SelectedFilters'.
    return {...newPayload, sent_link_id: sentLinkId};
  } else {
    return lodash.omit(payload, ['event_types']);
  }
};

export const getJourneyDashboardName = (
  journeyId: string,
  eventId?: string,
): string => {
  if (journeyId && eventId) {
    return `journeys_${journeyId}_${eventId}`;
  } else if (journeyId) {
    return `journeys_${journeyId}`;
  } else {
    return 'journeys_all';
  }
};

export const getWorkflowDashboardName = (
  workflowId?: string,
  nodeId?: string,
): string => {
  if (workflowId && nodeId) {
    return `workflows_${workflowId}_${nodeId}`;
  } else if (workflowId) {
    return `workflows_${workflowId}`;
  } else {
    return 'workflows_all';
  }
};

export const EXPORT_ANALYTICS_JOURNEYS_ENGAGEMENT_URL =
  'data-exports/journeys-overview/journeys';

export const EXPORT_ANALYTICS_JOURNEYS_ENGAGEMENT_ALL_TOUCHPOINTS_URL =
  'data-exports/journeys-overview/journeys-with-touchpoints';

export const EXPORT_ANALYTICS_TOUCHPOINTS_ENGAGEMENT_URL =
  'data-exports/journeys-overview/touchpoints';

export const EXPORT_ANALYTICS_PEOPLES_ENGAGEMENT_URL =
  'data-exports/journeys-overview/peoples';

export const EXPORT_ANALYTICS_ALL_VOICEFLOWS_URL =
  'data-exports/analytics/all-voice-flows';

export const EXPORT_ANALYTICS_ALL_WORKFLOWS_URL =
  'data-exports/analytics/voice-workflows';

export const EXPORT_ANALYTICS_ALL_CALL_RECORDS_URL =
  'data-exports/analytics/voice-call-records';

export const getMenuOptionsForAllJourneyOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'bounced', label: t('Bounced', 'Bounced')},
  {key: 'bounced_percentage', label: t('Bounced_Rate', 'Bounced Rate')},
  {key: 'delivered', label: t('Delivered', 'Delivered')},
  {key: 'delivered_percentage', label: t('Delivered_Rate', 'Delivered Rate')},
  {key: 'sent', label: t('Sent', 'Sent')},
  {key: 'skipped', label: t('Skipped', 'Skipped')},
  {key: 'unsubscribed', label: t('Unsubscribed', 'Unsubscribed')},
  {
    key: 'unsubscribed_percentage',
    label: t('Unsubscribed_Rate', 'Unsubscribed Rate'),
  },
  {key: 'workflow_status', label: t('Journey_Status', 'Journey Status')},
  {key: 'workflow_id', label: t('Journey_ID', 'Journey ID')},
];

export const getMenuOptionsForAllJourneyOverviewTableOverviewSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'people_reached', label: t('People_Reached', 'People Reached')},
  {key: 'clicked', label: t('Clicked', 'Clicked')},
  {key: 'clicked_percentage', label: t('Clicked_Rate', 'Clicked Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForAllJourneyOverviewTableSMSSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [{key: 'replies', label: t('Replies', 'Replies')}];

export const getMenuOptionsForAllJourneyOverviewTableEmailSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'clicked', label: t('Clicked', 'Clicked')},
  {key: 'clicked_percentage', label: t('Clicked_Rate', 'Clicked Rate')},
  {key: 'opened', label: t('Opened', 'Opened')},
  {key: 'opened_percentage', label: t('Opened_Rate', 'Opened Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForJourneyTouchpointTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'bounced', label: t('Bounced', 'Bounced')},
  {key: 'bounced_percentage', label: t('Bounced_Rate', 'Bounced Rate')},
  {key: 'delivered', label: t('Delivered', 'Delivered')},
  {key: 'delivered_percentage', label: t('Delivered_Rate', 'Delivered Rate')},

  {key: 'sent', label: t('Sent', 'Sent')},
  {key: 'skipped', label: t('Skipped', 'Skipped')},
  {key: 'unsubscribed', label: t('Unsubscribed', 'Unsubscribed')},
  {
    key: 'unsubscribed_percentage',
    label: t('Unsubscribed_Rate', 'Unsubscribed Rate'),
  },
  {key: 'workflow_id', label: t('Journey_ID', 'Journey ID')},
  {key: 'event_id', label: t('Touchpoint_ID', 'Touchpoint ID')},
];

export const getMenuOptionsForJourneyTouchpointTableOverviewSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'clicked', label: t('Clicked', 'Clicked')},
  {key: 'people_reached', label: t('People_Reached', 'People Reached')},
  {key: 'clicked_percentage', label: t('Clicked_Rate', 'Clicked Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: 'Spam Reports'},
];

export const getMenuOptionsForJourneyTouchpointTableSMSSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [{key: 'replies', label: t('Replies', 'Replies')}];

export const getMenuOptionsForJourneyTouchpointTableEmailSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'clicked', label: t('Clicked', 'Clicked')},
  {key: 'clicked_percentage', label: t('Clicked_Rate', 'Clicked Rate')},
  {key: 'opened', label: t('Opened', 'Opened')},
  {key: 'opened_percentage', label: t('Opened_Rate', 'Opened Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForPeopleTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {key: 'bounced_reason', label: t('Bounced_Reason', 'Bounced Reason')},
  {key: 'email', label: t('Email', 'Email')},
  {key: 'entity_type', label: t('Entity_Type', 'Entity Type')},
  {key: 'phone', label: t('Phone', 'Phone')},
  {key: 'skip_reason', label: t('Skip_Reason', 'Skip Reason')},
  {key: 'status', label: t('Status', 'Status')},
  {key: 'time_bounced', label: t('Time_Bounced', 'Time Bounced')},
  {key: 'time_created', label: t('Time_Sent', 'Time Sent')},
  {key: 'time_delivered', label: t('Time_Delivered', 'Time Delivered')},
  {key: 'time_reply', label: t('Time_Replied', 'Time Replied')},
  {key: 'workflow_id', label: t('Journey_ID', 'Journey ID')},
  {key: 'event_id', label: t('Touchpoint_ID', 'Touchpoint ID')},
];

export const getMenuOptionsForPeopleTableOverviewSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'time_clicked', label: t('Time_Clicked', 'Time Clicked')},
];

export const getMenuOptionsForPeopleTableJ1SMSReplySpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'flagged',
    label: t('Flagged', 'Flagged'),
  },
  {
    key: 'sms_replies',
    label: t('SMS_Reply', 'SMS Reply'),
  },
];

export const getMenuOptionsForPeopleTableJ2SMSReplySpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'flagged',
    label: t('Flagged', 'Flagged'),
  },
  {
    key: 'replies',
    label: t('SMS_Reply', 'SMS Reply'),
  },
];

export const getMenuOptionsForPeopleTableEmailSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'time_clicked', label: t('Time_Clicked', 'Time Clicked')},
  {key: 'time_opened', label: t('Time_Opened', 'Time Opened')},
  {key: 'is_reminder', label: t('Is_Reminder', 'Is Reminder')},
];

/**
 * Formats a given date string to the custom format 'ddd, M/D/YY, h:mm A' in the specified timezone.
 *
 * @param {string} inputStr - The input date string in the format 'YYYY-MM-DD HH:mm:ss.SSSSSS'.
 * @param {string} agencyTimezone - The agency's timezone (e.g. 'America/New_York'). If not provided, defaults to UTC.
 * @param {string} dateFormat - The date format to use. If not provided, defaults to 'ddd, M/D/YY, h:mm A'.
 * @returns {string} - The formatted date string in the format in the agency timezone.
 */
export function formatToCustomDateString(
  inputStr: string,
  agencyTimezone?: string,
  dateFormat: string = 'ddd, M/D/YY, h:mm A',
  locale?: string,
): string {
  // Parse the input string to a Moment object in UTC
  const dt = moment.utc(inputStr).locale(locale || 'en');

  // Convert to agency timezone if provided, otherwise keep in UTC
  const dateInTimezone = agencyTimezone ? dt.tz(agencyTimezone) : dt;

  // Convert the Moment object to the desired string format
  return dateInTimezone.format(dateFormat);
}

export const WORKFLOW_PAGE_SIZE = 10;

export const getWorkflowDashboardTableList = ({
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'JourneysListState': query, sort, pageNumber
  listState = {},
  pageSize,
  workflowId,
  nodeId,
  commonPayload,
}: WorkflowTableListAPIPayload): WorkflowOverviewDataTableQuery => {
  const {sort: {sortBy, sortOrder} = {}, pageNumber, query} = listState;
  let apiQuery = {
    ...commonPayload,
    limit: pageSize,
    offset: (pageNumber - 1) * pageSize,
  };
  if (workflowId) {
    // @ts-expect-error - TS2561 - Object literal may only specify known properties, but 'workflow_id' does not exist in type '{ limit: number; offset: number; workflow_categories?: string[] | undefined; entity_data_filter?: unknown; workflow_status?: string[] | undefined; recipient_type?: string[] | undefined; workflow_ids?: string[] | undefined; ... 4 more ...; view?: string | undefined; }'. Did you mean to write 'workflow_ids'?
    apiQuery = {...apiQuery, workflow_id: workflowId};
  }
  if (nodeId) {
    // @ts-expect-error - TS2561 - Object literal may only specify known properties, but 'node_id' does not exist in type '{ limit: number; offset: number; workflow_categories?: string[] | undefined; entity_data_filter?: unknown; workflow_status?: string[] | undefined; recipient_type?: string[] | undefined; workflow_ids?: string[] | undefined; ... 4 more ...; view?: string | undefined; }'. Did you mean to write 'node_ids'?
    apiQuery = {...apiQuery, node_id: nodeId};
  }
  if (query) {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'query' does not exist in type '{ limit: number; offset: number; workflow_categories?: string[] | undefined; entity_data_filter?: unknown; workflow_status?: string[] | undefined; recipient_type?: string[] | undefined; workflow_ids?: string[] | undefined; ... 4 more ...; view?: string | undefined; }'.
    apiQuery = {...apiQuery, query};
  }
  if (sortBy && sortOrder !== 'original') {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sort' does not exist in type '{ limit: number; offset: number; workflow_categories?: string[] | undefined; entity_data_filter?: unknown; workflow_status?: string[] | undefined; recipient_type?: string[] | undefined; workflow_ids?: string[] | undefined; ... 4 more ...; view?: string | undefined; }'.
    apiQuery = {...apiQuery, sort: sortOrder === 'asc' ? sortBy : `-${sortBy}`};
  }
  return apiQuery;
};

export const EXPORT_ALL_SURVEYS = 'data-exports/analytics/all-surveys';
export const EXPORT_ALL_VOICEFLOWS = 'data-exports/analytics/all-voiceflows';

export const EXPORT_CANDIDATE_NPS = 'data-exports/analytics/candidate-nps';
export const EXPORT_CANDIDATE_NPS_SOURCE =
  'data-exports/analytics/source-agn-candidate-nps';

export const EXPORT_CLIENT_NPS = 'data-exports/analytics/client-nps';
export const EXPORT_CLIENT_NPS_SOURCE =
  'data-exports/analytics/source-agn-client-nps';

export const EXPORT_ALL_SURVEYS_CONVERSION_SUMMARY_DETAILS =
  'data-exports/all-surveys/conversion-summary';

export const EXPORT_ANALYTICS_WORKFLOWS_ENGAGEMENT_URL =
  'data-exports/workflows-overview/workflows';

export const EXPORT_ANALYTICS_NODES_ENGAGEMENT_URL =
  'data-exports/workflows-overview/nodes';

export const EXPORT_ANALYTICS_PEOPLES_WORKFLOW_ENGAGEMENT_URL =
  'data-exports/workflows-overview/peoples';

export const EXPORT_CRM_RECRUITER_PRODUCTIVITY =
  'data-exports/crm/recruiter-productivity';
export const EXPORT_CRM_JOB_STATUS =
  'data-exports/crm/hiring-pipeline/job-status';
export const EXPORT_CRM_INTERVIEW_SCHEDULE =
  'data-exports/crm/hiring-pipeline/interview-schedule';
export const EXPORT_CRM_PIPELINE_VELOCITY =
  'data-exports/crm/hiring-pipeline/pipeline-velocity';
export const EXPORT_CRM_PIPELINE_VELOCITY_CANDIDATE =
  'data-exports/crm/hiring-pipeline/pipeline-velocity-candidate-data';

export const EXPORT_ALL_SURVEYS_ALL_RESPONSES =
  'data-exports/all-surveys/all-responses-table';

export const EXPORT_ALL_SURVEYS_ALL_RESPONSES_MCQ =
  'data-exports/all-surveys/all-responses-table-mcq';

export const getMenuOptionsForAllWorkflowOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'bounced', label: t('Bounced', 'Bounced')},
  {key: 'bounced_percentage', label: t('Bounced_Rate', 'Bounced Rate')},
  {key: 'delivered', label: t('Delivered', 'Delivered')},
  {key: 'delivered_percentage', label: t('Delivered_Rate', 'Delivered Rate')},
  {key: 'sent', label: t('Sent', 'Sent')},
  {key: 'skipped', label: t('Skipped', 'Skipped')},
  {key: 'unsubscribed', label: t('Unsubscribed', 'Unsubscribed')},
  {
    key: 'unsubscribed_percentage',
    label: t('Unsubscribed_Rate', 'Unsubscribed Rate'),
  },
  {key: 'workflow_status', label: t('Workflow_Status', 'Workflow Status')},
  {key: 'workflow_id', label: t('Workflow_ID', 'Workflow ID')},
];

export const getMenuOptionsForAllWorkflowOverviewTableOverviewSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'clicked', label: t('Clicked', 'Clicked')},
  {key: 'people_reached', label: t('People_Reached', 'People Reached')},
  {key: 'clicked_percentage', label: t('Clicked_Rate', 'Clicked Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForAllWorkflowOverviewTableSMSSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [{key: 'replies', label: t('Replies', 'Replies')}];

export const getMenuOptionsForAllWorkflowOverviewTableEmailSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'opened', label: t('Opened', 'Opened')},
  {key: 'opened_percentage', label: t('Opened_Rate', 'Opened Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForAllNodesOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'bounced', label: t('Bounced', 'Bounced')},
  {key: 'bounced_percentage', label: t('Bounced_Rate', 'Bounced Rate')},
  {key: 'delivered', label: t('Delivered', 'Delivered')},
  {key: 'delivered_percentage', label: t('Delivered_Rate', 'Delivered Rate')},
  {key: 'sent', label: t('Sent', 'Sent')},
  {key: 'skipped', label: t('Skipped', 'Skipped')},
  {key: 'unsubscribed', label: t('Unsubscribed', 'Unsubscribed')},
  {
    key: 'unsubscribed_percentage',
    label: t('Unsubscribed_Rate', 'Unsubscribed Rate'),
  },
  {key: 'workflow_status', label: t('Workflow_Status', 'Workflow Status')},
  {key: 'workflow_id', label: t('Workflow_ID', 'Workflow ID')},
  {key: 'node_id', label: t('Node_ID', 'Node ID')},
];

export const getMenuOptionsForAllNodesOverviewTableOverviewSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'clicked', label: t('Clicked', 'Clicked')},
  {key: 'people_reached', label: t('People_Reached', 'People Reached')},
  {key: 'clicked_percentage', label: t('Clicked_Rate', 'Clicked Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForAllNodesOverviewTableSMSSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [{key: 'replies', label: t('Replies', 'Replies')}];

export const getMenuOptionsForAllNodesOverviewTableEmailSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'opened', label: t('Opened', 'Opened')},
  {key: 'opened_percentage', label: t('Opened_Rate', 'Opened Rate')},
  {key: 'spam_percentage', label: t('Spam_Rate', 'Spam Rate')},
  {key: 'spam_reports', label: t('Spam_Reports', 'Spam Reports')},
];

export const getMenuOptionsForAllPeoplesOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {key: 'bounced_reason', label: t('Bounced_Reason', 'Bounced Reason')},
  {key: 'current_status', label: t('Status', 'Status')},
  {key: 'email', label: t('Email', 'Email')},
  {key: 'entity_type', label: t('Entity_Type', 'Entity Type')},
  {key: 'name', label: t('Name', 'Name')},
  {key: 'phone_number', label: t('Phone_Number', 'Phone Number')},
  {key: 'recipient_entity_id', label: t('Recipient_ID', 'Recipient ID')},
  {key: 'skip_reason', label: t('Skip_Reason', 'Skip Reason')},
  {key: 'time_bounced', label: t('Time_Bounced', 'Time Bounced')},
  {key: 'time_created', label: t('Time_Sent', 'Time Sent')},
  {key: 'time_delivered', label: t('Time_Delivered', 'Time Delivered')},
  {key: 'time_reply', label: t('Time_Replied', 'Time Replied')},
  {key: 'workflow_id', label: t('Workflow_ID', 'Workflow ID')},
  {key: 'node_id', label: t('Node_ID', 'Node ID')},
  {key: 'node_name', label: t('Node_Name', 'Node Name')},
];

export const getMenuOptionsForAllPeoplesOverviewTableOverviewSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'time_clicked', label: t('Time_Clicked', 'Time Clicked')},
];

export const getMenuOptionsForAllPeoplesOverviewTableEmailSpecific = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'time_clicked', label: t('Time_Clicked', 'Time Clicked')},
  {key: 'time_opened', label: t('Time_Opened', 'Time Opened')},
];

export const workflowTableColumnsQuery = {
  product: 'workflows',
  dashboard: 'engagement-and-deliverability',
  sub_dashboard: 'workflow-view',
  table: 'workflows-data-table',
} as const;

export const nodeTableColumnsQuery = {
  product: 'workflows',
  dashboard: 'engagement-and-deliverability',
  sub_dashboard: 'node-view',
  table: 'nodes-data-table',
} as const;

export const peopleWorkflowTableColumnsQuery = {
  product: 'workflows',
  dashboard: 'engagement-and-deliverability',
  sub_dashboard: 'people-view',
  table: 'peoples-data-table',
} as const;

export const KPIMetricsJourneyClickInsightsOrder = [
  'sent',
  'clicked',
  'click_rate',
  'click_to_open_rate',
] as const;

export const getTypeOfJourneyClickInsightsMetric = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'link_sent_and_clicked',
    label: t('Link_Sent_And_Clicked', 'Link Sent And Clicked'),
  },
  {key: 'click_rate', label: t('Click_Rates', 'Click Rates')},
  {
    key: 'click_to_open_rate',
    label: t('Click_To_Open_Rate', 'Click To Open Rate'),
  },
];

export const journeyClickInsightsTableColumnsQuery = {
  product: 'journeys',
  dashboard: 'external-link-clicks',
  sub_dashboard: 'clicks-view',
  table: 'clicks-data-table',
} as const;

export const urlClickInsightsTableColumnsQuery = {
  product: 'journeys',
  dashboard: 'external-link-clicks',
  sub_dashboard: 'people-view',
  table: 'people-data-table',
} as const;

export const getMenuOptionsForJourneyClickInsightDataTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'click_rate',
    label: t('Click_Rate', 'Click Rate'),
  },
  {
    key: 'click_to_open_rate',
    label: t('Click_To_Open_Rate', 'Click To Open Rate'),
  },
  {
    key: 'clicked',
    label: t('Clicked', 'Clicked'),
  },
  {
    key: 'sent',
    label: t('Sent', 'Sent'),
  },
];

export const getMenuOptionsForURLClickInsightDataTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {
    key: 'link_name',
    label: t('Link_Name', 'Link Name'),
  },
  {
    key: 'sent_time',
    label: t('Sent_Time', 'Sent Time'),
  },
  {
    key: 'delivered_time',
    label: t('Delivered_Time', 'Delivered Time'),
  },
  {
    key: 'count_of_clicks',
    label: t('Count_of_Clicks', 'Count of Clicks'),
  },
  {
    key: 'last_clicked_time',
    label: t('Last_Clicked_Time', 'Last Clicked Time'),
  },
];

export const getClickInsightTableList = ({
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'JourneysListState': query, sort, pageNumber
  listState = {},
  pageSize,
  sentLink,
  commonPayload,
}: JourneyTableListAPIPayload): JourneyOverviewDataTableQuery => {
  const {sort: {sortBy, sortOrder} = {}, pageNumber, query} = listState;
  let apiQuery = {
    ...commonPayload,
    limit: pageSize,
    offset: (pageNumber - 1) * pageSize,
  };
  if (sentLink) {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sent_link' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'.
    apiQuery = {...apiQuery, sent_link: sentLink};
  }

  if (query) {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'query' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'.
    apiQuery = {...apiQuery, query};
  }
  if (sortBy && sortOrder !== 'original') {
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sort' does not exist in type '{ limit: number; offset: number; journey_lifecycle_goals?: string[] | undefined; journey_business_goals?: string[] | undefined; journey_categories?: string[] | undefined; entity_data_filter?: unknown; ... 10 more ...; channels?: string[] | undefined; }'.
    apiQuery = {...apiQuery, sort: sortOrder === 'asc' ? sortBy : `-${sortBy}`};
  }
  return apiQuery;
};

export const EXPORT_ANALYTICS_JOURNEYS_CLICK_INSIGHTS_URL =
  'data-exports/external-link-clicks-details';

export const EXPORT_ANALYTICS_JOURNEYS_CLICK_RECIPIENTS_INSIGHTS_URL =
  'data-exports/external-link-clicks/peoples';

// Function to save banner acknowledgment status in localStorage
export const saveBannerAcknowledgment = ({
  currentUserId,
  workflowId,
  swimlaneId,
}: {
  workflowId: string;
  currentUserId: string;
  swimlaneId: string;
}): void => {
  try {
    const key = `${currentUserId}_${workflowId}_${swimlaneId}`;
    const value = true;
    localStorage.setItem(key, value.toString()); // Convert boolean to string and save
  } catch (err: any) {
    console.error(err);
  }
};

// Function to retrieve banner acknowledgment status from localStorage
export const getBannerAcknowledgment = ({
  workflowId,
  currentUserId,
  swimlaneId,
}: {
  workflowId: string;
  currentUserId: string;
  swimlaneId: string;
}): boolean => {
  try {
    const key = `${currentUserId}_${workflowId}_${swimlaneId}`;
    const value = localStorage.getItem(key);
    return value === 'true'; // Convert string back to boolean
  } catch (err: any) {
    console.error(err);
    return false;
  }
};

export const transformNPSTableData = (
  input: NPSOverviewDataTable | null,
): NPSOverviewDataTable => {
  const {data = [], total_count = 0} = lodash.cloneDeep(input) ?? {};
  const entityKeys: Set<string> = new Set();

  const transformedData =
    data.map((row) => {
      // Collect all keys from the row itself

      if (row.entity_attributes) {
        // Collect all keys from entity_attributes
        Object.keys(row.entity_attributes).forEach((key) =>
          entityKeys.add(key),
        );

        // Merge entity_attributes into the main row
        Object.assign(row, row.entity_attributes);

        // Remove the entity_attributes property
        delete row.entity_attributes;
      }

      return row;
    }) || [];

  const allAttributesColumn = Array.from(entityKeys);

  // Return the transformed table data
  return {total_count, transformedData, allAttributesColumn};
};

export const transformSurveyV2TableData = (
  input: SurveyV2AllResponsesResponse | null,
) => {
  const {data = [], total_count = 0} = lodash.cloneDeep(input) ?? {};
  const entityKeys: Set<string> = new Set();

  const transformedData =
    data.map((row) => {
      // Collect all keys from the row itself

      if (row.entity_attributes) {
        // Collect all keys from entity_attributes
        Object.keys(row.entity_attributes).forEach((key) =>
          entityKeys.add(key),
        );

        // Merge entity_attributes into the main row
        Object.assign(row, row.entity_attributes);

        // Remove the entity_attributes property
        delete (row as any).entity_attributes;
      }

      return row;
    }) || [];

  const allAttributesColumn = Array.from(entityKeys);

  // Return the transformed table data
  return {total_count, transformedData, allAttributesColumn};
};

export const npsClientTableColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'nps',
  sub_dashboard: 'client-view',
  table: 'client-data-table',
} as const;

export const npsSourceAgnosticClientTableColumnsQuery = {
  product: 'nps-dashboard',
  dashboard: 'nps-dashboard',
  sub_dashboard: 'client-view',
  table: 'client-data-table',
} as const;

export const npsCandidateTableColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'nps',
  sub_dashboard: 'candidate-view',
  table: 'candidate-data-table',
} as const;

export const npsSourceAgnosticCandidateTableColumnsQuery = {
  product: 'nps-dashboard',
  dashboard: 'nps-dashboard',
  sub_dashboard: 'candidate-view',
  table: 'candidate-data-table',
} as const;

export const conversionSummaryTableColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'surveys',
  sub_dashboard: 'conversion-summary-view',
  table: 'conversion-summary-data-table',
} as const;

export const allResponsesTableColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'surveys',
  sub_dashboard: 'all-responses',
  table: 'all-responses-table',
} as const;

export const npsCandidateChartColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'nps',
  sub_dashboard: 'candidate-view',
  table: 'candidate-data-table',
} as const;

export const npsClientChartColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'nps',
  sub_dashboard: 'client-view',
  table: 'client-data-table',
} as const;

export const recruiterTableColumnsQuery = {
  product: 'crm',
  dashboard: 'recruiter-productivity',
  sub_dashboard: 'recruiter-data',
  table: 'recruiter_data-table',
} as const;

export const interviewScheduleTableColumnsQuery = {
  product: 'crm',
  dashboard: 'hiring-pipeline',
  sub_dashboard: 'interview-schedule',
  table: 'interview-schedule-table',
} as const;

export const getMenuOptionsForInterviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'job_id', label: t('Job_ID', 'Job ID')},
  {key: 'job_code', label: t('Job_Code', 'Job Code')},
  {key: 'job_title', label: t('Job_Title', 'Job Title')},
  {
    key: 'interviews_scheduled',
    label: t('Interview_Scheduled', 'Interview Scheduled'),
  },
  {
    key: 'interview_review_completed',
    label: t('Interview_Review_Completed', 'Interview Review Completed'),
  },
  {key: 'review_delay', label: t('Review_Delay', 'Review Delay')},
  {key: 'recruiter', label: t('Recruiter_Name', 'Recruiter Name')},
  {
    key: 'hiring_manager',
    label: t('Hiring_Manager_Name', 'Hiring Manager Name'),
  },
];

export const interviewTableColumnsQuery = {
  product: 'crm',
  dashboard: 'hiring-pipeline',
  sub_dashboard: 'interview-schedule',
  table: 'interview-schedule-table',
} as const;

export const jobStatusTableColumnsQuery = {
  product: 'crm',
  dashboard: 'hiring-pipeline',
  sub_dashboard: 'job-status',
  table: 'job-status-table',
} as const;

export const pipelineVelocityTableColumnsQuery = {
  product: 'crm',
  dashboard: 'hiring-pipeline',
  sub_dashboard: 'pipeline-velocity',
  table: 'pipeline-velocity-table',
} as const;

export const pipelineVelocityDrilldownTableColumnsQuery = {
  product: 'crm',
  dashboard: 'hiring-pipeline',
  sub_dashboard: 'pipeline-velocity',
  table: 'pipeline-velocity-candidate-table',
} as const;

export const allApplicantsTableColumnsQuery = {
  product: 'crm',
  dashboard: 'all-applicants',
  sub_dashboard: 'all-applicants',
  table: 'all-applicants-table',
} as const;

export const getMenuOptionsForNPSOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {
    key: 'email',
    label: t('Email', 'Email'),
  },
  {
    key: 'phone_number',
    label: t('Phone_Number', 'Phone Number'),
  },
  {key: 'change', label: t('Change', 'Change')},
  {key: 'current_nps', label: t('Current_NPS', 'Current NPS')},
  {key: 'current_nps_time', label: t('Current_NPS_Time', 'Current NPS Time')},
  {key: 'previous_nps', label: t('Previous_NPS', 'Previous NPS')},
  {
    key: 'previous_nps_time',
    label: t('Previous_NPS_Time', 'Previous NPS Time'),
  },
  {key: 'journey_name', label: 'Journey Name'},
  {key: 'touchpoint_name', label: 'Touchpoint Name'},
];

export const getMenuOptionsForNPSSourceOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {
    key: 'email',
    label: t('Email', 'Email'),
  },
  {
    key: 'phone_number',
    label: t('Phone_Number', 'Phone Number'),
  },
  {key: 'change', label: t('Change', 'Change')},
  {key: 'current_nps', label: t('Current_NPS', 'Current NPS')},
  {key: 'current_nps_time', label: t('Current_NPS_Time', 'Current NPS Time')},
  {key: 'previous_nps', label: t('Previous_NPS', 'Previous NPS')},
  {
    key: 'previous_nps_time',
    label: t('Previous_NPS_Time', 'Previous NPS Time'),
  },
  {key: 'journey_name', label: 'Source Name'},
  {key: 'touchpoint_name', label: 'Survey Name'},
  {key: 'touchpoint_id', label: 'Survey ID'},
];

export const getMenuOptionsForRecruiterTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'recruiter_name',
    label: t('Recruiter_Name', 'Recruiter Name'),
    disabled: true,
  },
  {key: 'jobs_assigned', label: t('Jobs_Assigned', 'Jobs Assigned')},
  {
    key: 'positions_assigned',
    label: t('Positions_Assigned', 'Positions Assigned'),
  },
  {key: 'total_applicants', label: t('Total_Applicants', 'Total Applicants')},
  {key: 'shortlisted', label: t('Shortlisted', 'Shortlisted')},
  {
    key: 'interview_scheduled',
    label: t('Interview_Scheduled', 'Interview Scheduled'),
  },
  {key: 'offers_sent', label: t('Offers_Sent', 'Offers Sent')},
  {key: 'hires', label: t('Hires', 'Hires')},
  {key: 'joined', label: t('Joined_or_Onboarded', 'Joined (or Onboarded)')},
  {key: 'selection_ratio', label: t('Selection_Ratio', 'Selection Ratio')},
  {key: 'joining_ratio', label: t('Joining_Ratio', 'Joining Ratio')},
  {key: 'last_activity', label: t('Last_Activity', 'Last Activity')},
];

export const getMenuOptionsForJobTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'job_id', label: t('Job_ID', 'Job ID')},
  {key: 'job_code', label: t('Job_Code', 'Job Code')},
  {key: 'job_title', label: t('Job_Title', 'Job Title')},
  {key: 'location', label: t('Location', 'Location')},
  {key: 'open_for', label: t('Open_For_in_days', 'Open For (in days)')},
  {key: 'recruiter', label: t('Recruiter', 'Recruiter')},
  {key: 'hiring_manager', label: t('Hiring_Manager', 'Hiring Manager')},
  {key: 'department', label: t('Department', 'Department')},
  {key: 'open_date', label: t('Open_Date', 'Open Date')},
  {key: 'onhold_date', label: t('Onhold_Date', 'Onhold Date')},
  {key: 'rejected_date', label: t('Rejected_Date', 'Rejected Date')},
  {
    key: 'approval_pending_date',
    label: t('Approval_Pending_Date', 'Approval Pending Date'),
  },
  {key: 'cancelled_date', label: t('Cancelled_Date', 'Cancelled Date')},
  {key: 'closed_date', label: t('Closed_Date', 'Closed Date')},
  {key: 'draft_date', label: t('Draft_Date', 'Draft Date')},
];

export const NPSKpiMetricsOrder = [
  'score',
  'promoters',
  'detractors',
  'passive',
] as const;

export const getIconNameForNpsSurveyMetrics = (
  metricKey: string,
): {
  iconName: string;
  iconSemantic: AlertSemanticType;
} => {
  switch (metricKey) {
    case 'score': {
      return {iconName: 'gauge', iconSemantic: 'neutral'};
    }
    case 'promoters': {
      return {iconName: 'face-smile', iconSemantic: 'success'};
    }
    case 'passive': {
      return {iconName: 'face-expressionless', iconSemantic: 'information'};
    }
    case 'detractors': {
      return {iconName: 'face-frown', iconSemantic: 'danger'};
    }
    default:
      return {iconName: 'gauge', iconSemantic: 'neutral'};
  }
};

export const SurveysKpiMetricsOrder = [
  'surveys_sent',
  'surveys_started',
  'surveys_completed',
  'completion_time',
] as const;

export const ConversionSummaryMetricsOrder = [
  'surveys_sent',
  'surveys_started',
  'surveys_partially_completed',
  'surveys_completed',
] as const;

export const EngagementKpiMetricsOrder = [
  'sent',
  'delivered',
  'opened',
  'skipped',
  'bounced',
  'replies',
  'unsubscribed',
  'spam',
] as const;

export const getMenuOptionsForAllSurveysTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'avg_completion_time',
    label: t('Average_Completion_Time', 'Average Completion Time'),
  },
  {key: 'channel', label: t('Channel', 'Channel')},
  {key: 'journey_name', label: t('Journey_Name', 'Journey Name')},
  {
    key: 'surveys_completed',
    label: t('Surveys_Completed', 'Surveys Completed'),
  },
  {
    key: 'surveys_completed_rate',
    label: t('Surveys_Completed_Rate', 'Surveys Completed Rate'),
  },
  {
    key: 'surveys_reminders_sent',
    label: t('Surveys_Reminders_Sent', 'Surveys Reminders Sent'),
  },
  {key: 'surveys_sent', label: t('Surveys_Sent', 'Surveys Sent')},
  {key: 'surveys_started', label: t('Surveys_Started', 'Surveys Started')},
  {
    key: 'surveys_started_rate',
    label: t('Surveys_Started_Rate', 'Surveys Started Rate'),
  },
  {
    key: 'journey_id',
    label: t('Journey_ID', 'Journey ID'),
  },
  {
    key: 'touchpoint_id',
    label: t('Touchpoint_ID', 'Touchpoint ID'),
  },
];

export const getMenuOptionsForAllVoiceflowsTable = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {key: 'retell_bot_id', label: t('Voiceflow_ID', 'Voiceflow ID')},
  {key: 'workflow_list', label: t('Used_in_Workflows', 'Used in Workflows')},
  {
    key: 'calls_attempted',
    label: t('Attempted', 'Attempted'),
  },
  {
    key: 'calls_answered',
    label: t('Answered', 'Answered'),
  },
  {
    key: 'calls_answered_rate',
    label: t('Answer_Rate', 'Answer Rate'),
  },
  {
    key: 'calls_completed',
    label: t('Completed', 'Completed'),
  },
  {
    key: 'calls_completed_rate',
    label: t('Completion_Rate', 'Completion Rate'),
  },
  {
    key: 'calls_dropped',
    label: t('Dropped', 'Dropped'),
  },
  {
    key: 'calls_dropped_by_agent',
    label: t('Dropped_by_Agent', 'Dropped by Agent'),
  },
  {
    key: 'calls_dropped_by_user',
    label: t('Dropped_by_User', 'Dropped by User'),
  },
  {
    key: 'calls_dropped_rate',
    label: t('Drop_Rate', 'Drop Rate'),
  },
  {
    key: 'calls_reschedule',
    label: t('Rescheduled', 'Rescheduled'),
  },
  {
    key: 'calls_reschedule_rate',
    label: t('Reschedule_Rate', 'Reschedule Rate'),
  },
  {
    key: 'total_voicemail',
    label: t('Voicemails', 'Voicemails'),
  },
  {
    key: 'total_voicemail_rate',
    label: t('Voicemails_Rate', 'Voicemails Rate'),
  },
  {
    key: 'no_response',
    label: t('No_Responses', 'No Responses'),
  },
  {
    key: 'no_response_rate',
    label: t('No_Response_Rate', 'No Response Rate'),
  },
  {
    key: 'total_invalid_number',
    label: t('Invalid_Numbers', 'Invalid Numbers'),
  },
  {
    key: 'avg_call_duration',
    label: t('Average_Call_Duration', 'Average Call Duration'),
  },
  {
    key: 'median_call_duration',
    label: t('Median_Call_Duration', 'Median Call Duration'),
  },
];

export const getMenuOptionsForAllWorkflowsTable = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {key: 'workflow_name', label: t('Workflow_Name', 'Workflow Name')},
  {key: 'workflow_id', label: t('Workflow_ID', 'Workflow ID')},
  {key: 'primary_entity_type', label: t('Focus_Entity', 'Focus Entity')},
  {key: 'voiceflow_list', label: t('Voiceflows', 'Voiceflows')},
  {
    key: 'calls_attempted',
    label: t('Attempted', 'Attempted'),
  },
  {
    key: 'calls_answered',
    label: t('Answered', 'Answered'),
  },
  {
    key: 'calls_answered_rate',
    label: t('Answer_Rate', 'Answer Rate'),
  },
  {
    key: 'calls_completed',
    label: t('Completed', 'Completed'),
  },
  {
    key: 'calls_completed_rate',
    label: t('Completion_Rate', 'Completion Rate'),
  },
  {
    key: 'calls_dropped',
    label: t('Dropped', 'Dropped'),
  },
  {
    key: 'calls_dropped_by_agent',
    label: t('Dropped_by_Agent', 'Dropped by Agent'),
  },
  {
    key: 'calls_dropped_by_user',
    label: t('Dropped_by_User', 'Dropped by User'),
  },
  {
    key: 'calls_dropped_rate',
    label: t('Drop_Rate', 'Drop Rate'),
  },
  {
    key: 'calls_reschedule',
    label: t('Rescheduled', 'Rescheduled'),
  },
  {
    key: 'calls_reschedule_rate',
    label: t('Reschedule_Rate', 'Reschedule Rate'),
  },
  {
    key: 'total_voicemail',
    label: t('Voicemails', 'Voicemails'),
  },
  {
    key: 'total_voicemail_rate',
    label: t('Voicemails_Rate', 'Voicemails Rate'),
  },
  {
    key: 'no_response',
    label: t('No_Responses', 'No Responses'),
  },
  {
    key: 'no_response_rate',
    label: t('No_Response_Rate', 'No Response Rate'),
  },
  {
    key: 'total_invalid_number',
    label: t('Invalid_Numbers', 'Invalid Numbers'),
  },
  {
    key: 'avg_call_duration',
    label: t('Average_Call_Duration', 'Average Call Duration'),
  },
  {
    key: 'median_call_duration',
    label: t('Median_Call_Duration', 'Median Call Duration'),
  },
];

export const getMenuOptionsForAllCallRecordsTable = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {key: 'transcript_link', label: t('Transcript', 'Transcript')},
  {key: 'call_duration', label: t('Call_Duration', 'Call Duration')},
  {key: 'recipient_email', label: t('Email', 'Email')},
  {key: 'recipient_phone_number', label: t('Phone', 'Phone')},
  {
    key: 'recipient_entity_type',
    label: t('Recipient_Entity', 'Recipient Entity'),
  },
  {key: 'retell_bot_name', label: t('Voicebot_Name', 'Voicebot Name')},
  {key: 'status', label: t('Status', 'Status')},
  {key: 'workflow_id', label: t('Workflow_ID', 'Workflow ID')},
  {key: 'workflow_name', label: t('Workflow_Name', 'Workflow Name')},
  {key: 'node_name', label: t('Node_Name', 'Node Name')},
  {
    key: 'primary_entity_type',
    label: t('Workflow_Focus_Entity', 'Workflow Focus Entity'),
  },
  {key: 'time_attempted', label: t('Attempt_Time', 'Attempt Time')},
  {key: 'time_started', label: t('Answer_Time', 'Answer Time')},
  {key: 'time_dropped', label: t('Dropped_Time', 'Dropped Time')},
  {key: 'time_completed', label: t('Completed_Time', 'Completed Time')},
  {
    key: 'sense_phone_number',
    label: t('Call_From_Number', 'Call From Number'),
  },
  {key: 'disconnection_reason', label: t('Tags_Reason', 'Tags / Reason')},
];

export const allVoiceflowsTableColumnsQuery = {
  product: 'voice_call',
  dashboard: 'voice_call',
  sub_dashboard: 'voice-workflow-view',
  table: 'voice-workflow-data-table',
} as const;

export const allWorkflowsTableColumnsQuery = {
  product: 'voice_call',
  dashboard: 'voice_call',
  sub_dashboard: 'workflow-voice-view',
  table: 'workflow-voice-data-table',
} as const;

export const allCallRecordsTableColumnsQuery = {
  product: 'voice_call',
  dashboard: 'voice_call',
  sub_dashboard: 'voice-call-view',
  table: 'voice-call-data-table',
} as const;

export const getMenuOptionsForAllResponsesTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {key: 'name', label: t('Recipient_Name', 'Recipient Name')},
  {key: 'email', label: t('Email', 'Email')},
  {key: 'phone_number', label: t('Phone_Number', 'Phone Number')},
  {key: 'recipient_entity', label: t('Recipient_Entity', 'Recipient Entity')},
  {key: 'journey_name', label: t('Journey_Name', 'Journey Name')},
  {key: 'touchpoint_name', label: t('Touchpoint_Name', 'Touchpoint Name')},
  {key: 'question_type', label: t('Question_Type', 'Question Type')},
  {key: 'question_text', label: t('Question_Text', 'Question Text')},
  {key: 'answer', label: t('Answer', 'Answer')},
  {
    key: 'completion_status',
    label: t('Completion_Status', 'Completion Status'),
  },
  {key: 'sent_on', label: t('Sent_On', 'Sent On')},
  {key: 'started_on', label: t('Started_On', 'Started On')},
  {key: 'completed_on', label: t('Completed_On', 'Completed On')},
  {key: 'focus_entity', label: t('Focus_Entity', 'Focus Entity')},
  {key: 'recipient_resource', label: t('Respondent_Type', 'Respondent Type')},
  {key: 'journey_id', label: t('Journey_ID', 'Journey ID')},
  {key: 'touchpoint_id', label: t('Touchpoint_ID', 'Touchpoint ID')},
];

export const getMenuOptionsForRecipientTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'entityVariable', label: t('Entity_Variable', 'Entity Variable')},
  {
    key: 'name',
    label: t('Recipient_Name', 'Recipient Name'),
  },
  {
    key: 'completion_status',
    label: t('Completion_Status', 'Completion Status'),
  },
  {
    key: 'sent_on',
    label: t('Sent_On', 'Sent On'),
  },
  {
    key: 'started_on',
    label: t('Started_On', 'Started On'),
  },
  {
    key: 'completed_on',
    label: t('Completed_On', 'Completed On'),
  },
  {
    key: 'reminders_sent',
    label: t('Reminders_Sent', 'Reminders Sent'),
  },
  {key: 'journey_id', label: t('Journey_ID', 'Journey ID')},
  {key: 'touchpoint_id', label: t('Touchpoint_ID', 'Touchpoint ID')},
];

export const allSurveysTableColumnsQuery = {
  product: 'nps-and-surveys',
  dashboard: 'surveys',
  sub_dashboard: 'surveys-view',
  table: 'surveys-data-table',
} as const;

export const getTableHeaderLabel = ({
  tableColumnsList,
  key,
}: {
  tableColumnsList: Array<MenuOption>;
  key: string;
}): string => {
  return (
    tableColumnsList.find((optionItem) => optionItem.key === key)?.label ||
    convertStringCase(key)
  );
};

export const getSurveyTabs = (
  t: (arg1: string, arg2: string) => string,
): SurveyNPSActiveTabs => [
  {
    id: 'all-surveys',
    label: t('All_Surveys', 'All Surveys'),
  },
  {
    id: 'flagged-responses',
    label: t('Flagged_Responses', 'Flagged Responses'),
  },
  {
    id: 'all-responses',
    label: t('All_Responses', 'All Responses'),
  },
];

export const getCandidateNpsTabs = (
  t: (arg1: string, arg2: string) => string,
): SurveyNPSActiveTabs => [
  {
    id: 'candidate-nps',
    label: t('Candidate_NPS', 'Candidate NPS'),
  },
];

export const getClientNpsTabs = (
  t: (arg1: string, arg2: string) => string,
): SurveyNPSActiveTabs => [
  {
    id: 'client-nps',
    label: t('Client_NPS', 'Client NPS'),
  },
];

export const secondsToTimeBreakdown = (seconds: number): string => {
  const secondsInAMinute = 60;
  const secondsInAnHour = 3600; // 60 minutes * 60 seconds
  const secondsInADay = 86400; // 24 hours * 60 minutes * 60 seconds
  const secondsInAWeek = 604800; // 7 days * 24 hours * 60 minutes * 60 seconds

  if (seconds === 0) {
    return '0'; // Display 0 if seconds are 0
  }

  if (seconds < secondsInAMinute) {
    return '<1 m'; // Display <1 m
  }

  // Convert to the nearest minute
  const minutes = Math.round(seconds / secondsInAMinute);
  if (minutes < 60) {
    return `${minutes} m`; // Display in minutes
  }

  // Convert to the nearest hour
  const hours = Math.round(seconds / secondsInAnHour);
  if (hours < 24) {
    return `${hours} h`; // Display in hours
  }

  // Convert to the nearest day
  const days = Math.round(seconds / secondsInADay);
  if (days < 7) {
    return `${days} d`; // Display in days
  }

  // Convert to the nearest week
  const weeks = Math.round(seconds / secondsInAWeek);
  return `${weeks} w`; // Display in weeks
};

export const formatTimeFromHours = (hours: number): string => {
  if (hours === 0) {
    return '0'; // Display 0 if hours are 0
  }

  // Convert to minutes if less than 1 hour
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return minutes === 0 ? '<1 m' : `${minutes} m`;
  }

  // Display in hours if less than 24 hours
  if (hours < 24) {
    return `${Math.round(hours)} h`;
  }

  // Convert to days for anything 1 day or more
  const days = Math.round(hours / 24);
  return `${days} d`;
};

export const JOB_METRICS = [
  'total_applicants',
  'shortlisted',
  'interview_scheduled',
  'offers_sent',
] as const;

export const getJobMetricTitle = (
  metric: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metric) {
    case 'total_applicants': {
      return t('Total_Applicants', 'Total Applicants');
    }
    case 'shortlisted': {
      return t('Shortlisted', 'Shortlisted');
    }
    case 'interview_scheduled': {
      return t('Interviews_Scheduled', 'Interviews Scheduled');
    }
    case 'offers_sent': {
      return t('Offers_Sent', 'Offers Sent');
    }
    default: {
      return '';
    }
  }
};

export const getRecruiterTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: string;
  label: string;
}> => [
  {id: 'details', label: t('Recruiter_Details', 'Recruiter Details')},
  {id: 'jobs', label: t('Jobs', 'Jobs')},
];

export const RECRUITER_PERSONAL_DETAILS = [
  'full_name',
  'first_name',
  'last_name',
  'department',
  'manager',
] as const;

export const getDetailLabelFromKey = (
  key: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (key) {
    case 'full_name': {
      return t('Full_Name', 'Full Name');
    }
    case 'first_name': {
      return t('First_Name', 'First Name');
    }
    case 'middle_name': {
      return t('Middle_Name', 'Middle Name');
    }
    case 'last_name': {
      return t('Last_Name', 'Last Name');
    }
    case 'department': {
      return t('Department', 'Department');
    }
    case 'manager': {
      return t('Manager_or_Supervisor', 'Manager or Supervisor');
    }
    default: {
      return '';
    }
  }
};

export function convertValuesToString(obj: MetricDataObject): MetricData {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]: [any, any]) => [key, String(value)]),
  );
}

export const PERF_METRICS = [
  'total_applicants',
  'shortlisted',
  'positions_assigned',
] as const;

export const getRecruiterMetricTitle = (
  metric: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metric) {
    case 'total_applicants': {
      return t('Total_Applicants', 'Total Applicants');
    }
    case 'positions_assigned': {
      return t('Positions_assigned', 'Positions assigned');
    }
    case 'shortlisted': {
      return t('Shortlisted_Candidates', 'Shortlisted Candidates');
    }
    default: {
      return '';
    }
  }
};

export const GetRecruiterMetricTopValue = (
  metricKey: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (metricKey) {
    case 'total_recruiters': {
      return t('Total_Recruiters', 'Total Recruiters');
    }
    case 'unassigned_recruiters': {
      return t('Unassigned_Recruiters', 'Unassigned Recruiters');
    }
    case 'never_logged_in': {
      return t('Never_logged_in', 'Never logged in');
    }
    default:
      return '';
  }
};

export const JOB_STATUS = [
  'TEMPLATE',
  'DRAFT',
  'APPROVAL_PENDING',
  'OPEN',
  'ONHOLD',
  'CANCELLED',
  'REJECTED',
  'CLOSED',
  'FILLED',
  'ARCHIVED',
  'OTHERS',
  'ALL',
] as const;

export const JOB_STATUS_SEMANTIC_MAP = {
  DRAFT: 'primary',
  CLOSED: 'secondary',
  OPEN: 'information',
  APPROVAL_PENDING: 'warning',
  ONHOLD: 'warning',
  REJECTED: 'danger',
  CANCELLED: 'danger',
  PUBLISHED: 'success',
  PUBLISHED_ON_CAREERS_PAGE: 'success',
  FILLED: 'primary',
  ARCHIVED: 'secondary',
  default: 'primary',
} as const;

export const getJobStatusLabelMap = (
  t: (arg1: string, arg2: string) => string,
): {
  DRAFT: string;
  CLOSED: string;
  OPEN: string;
  APPROVAL_PENDING: string;
  ONHOLD: string;
  REJECTED: string;
  CANCELLED: string;
  PUBLISHED: string;
  PUBLISHED_ON_CAREERS_PAGE: string;
  FILLED: string;
  ARCHIVED: string;
  default: string;
} => ({
  DRAFT: t('Draft', 'Draft'),
  CLOSED: t('Closed', 'Closed'),
  OPEN: t('Open', 'Open'),
  APPROVAL_PENDING: t('Approval_Pending', 'Approval Pending'),
  ONHOLD: t('On_Hold', 'On Hold'),
  REJECTED: t('Approval_Rejected', 'Approval Rejected'),
  CANCELLED: t('Cancelled', 'Cancelled'),
  PUBLISHED: t('Published', 'Published'),
  PUBLISHED_ON_CAREERS_PAGE: t(
    'Published_on_Careers_Page',
    'Published on Careers Page',
  ),
  FILLED: t('Filled', 'Filled'),
  ARCHIVED: t('Archived', 'Archived'),
  default: '',
});

export const JOB_STATUS_MAP = {
  DRAFT: 'DRAFT',
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
  APPROVAL_PENDING: 'APPROVAL_PENDING',
  ONHOLD: 'ONHOLD',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  PUBLISHED: 'PUBLISHED',
  PUBLISHED_ON_CAREERS_PAGE: 'PUBLISHED_ON_CAREERS_PAGE',
  FILLED: 'FILLED',
  ARCHIVED: 'ARCHIVED',
} as const;

export const formatResponseDate = (
  inputDate: string,
  language: string,
): string => {
  return moment.utc(inputDate).locale(language).format('MMM D, YYYY');
};

export const allSourcesKpiMetrics = [
  'best_performing_source',
  'worst_performing_source',
  'total_sources',
  'total_spend',
] as const;

export const subSourceKpiMetrics = [
  'best_performing_sub_source',
  'total_spend',
  'total_sub_sources',
  'worst_performing_sub_source',
] as const;

export const allSourcesTableColumnsQuery = {
  product: 'source-performance',
  dashboard: 'source-performance-analytics',
  sub_dashboard: 'all-source',
  table: 'all-source-data-table',
} as const;

export const subSourcesTableColumnsQuery = {
  product: 'source-performance',
  dashboard: 'source-performance-analytics',
  sub_dashboard: 'sub-source',
  table: 'sub-source-data-table',
} as const;

export const getMenuOptionsForAllSourcesTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'candidates_applied',
    label: t('Candidates_Applied', 'Candidates Applied'),
  },
  {
    key: 'candidates_offered',
    label: t('Candidates_Offered', 'Candidates Offered'),
  },
  {
    key: 'candidates_hired',
    label: t('Candidates_Hired', 'Candidates Hired'),
  },
  {
    key: 'candidates_joined',
    label: t('Candidates_Joined', 'Candidates Joined'),
  },
  {
    key: 'average_time_to_hire',
    label: t('Average_Time_To_Hire', 'Average Time To Hire'),
  },
  {
    key: 'hired_ratio',
    label: t('Hired_Ratio', 'Hired Ratio'),
  },
];

export const getMenuOptionsForSubSourcesTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'candidates_applied',
    label: t('Candidates_Applied', 'Candidates Applied'),
  },
  {
    key: 'candidates_offered',
    label: t('Candidates_Offered', 'Candidates Offered'),
  },
  {
    key: 'candidates_hired',
    label: t('Candidates_Hired', 'Candidates Hired'),
  },
  {
    key: 'average_time_to_hire',
    label: t('Average_Time_To_Hire', 'Average Time To Hire'),
  },
  {
    key: 'hired_ratio',
    label: t('Hired_Ratio', 'Hired Ratio'),
  },
];

export const agencyPerformanceKpiMetrics = [
  'jobs_agencies_working_on',
  'total_candidates_sourced',
  'total_candidates_hired',
  'total_candidates_joined',
  'average_time_to_hire',
] as const;

export const agencyPerformanceTableColumnsQuery = {
  product: 'source-performance',
  dashboard: 'source-performance-analytics',
  sub_dashboard: 'agency-performance',
  table: 'agency-performance-data-table',
} as const;

export const getMenuOptionsForAgencyPerformanceTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'job_title',
    label: t('Job_Title', 'Job Title'),
  },
  {
    key: 'job_id',
    label: t('Job_ID', 'Job ID'),
  },
  {
    key: 'agent_name',
    label: t('Agent_Name', 'Agent Name'),
  },
  {
    key: 'agency_name',
    label: t('Agency_Name', 'Agency Name'),
  },
  {
    key: 'cost',
    label: t('Cost', 'Cost'),
  },
  {
    key: 'Jobs',
    label: t('Jobs', 'Jobs'),
  },
  {
    key: 'job_type',
    label: t('Job_Type', 'Job Type'),
  },
  {
    key: 'job_status',
    label: t('Job_Status', 'Job Status'),
  },
  {
    key: 'job_location',
    label: t('Job_Location', 'Job Location'),
  },
  {
    key: 'candidates_sourced',
    label: t('Candidates_Sourced', 'Candidates Sourced'),
  },
  {
    key: 'candidates_offered',
    label: t('Candidates_Offered', 'Candidates Offered'),
  },
  {
    key: 'candidates_hired',
    label: t('Candidates_Hired', 'Candidates Hired'),
  },
  {
    key: 'average_time_to_hire',
    label: t('Average_Time_To_Hire', 'Average Time To Hire'),
  },
];

export const getAnalyticsSettingsTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  tabId: string;
  label?: string;
}> => [
  {tabId: 'performance', label: t('Performance', 'Performance')},
  {tabId: 'source', label: t('Source', 'Source')},
];
export const referralsOverviewTableColumnsQuery = {
  product: 'referrals',
  dashboard: 'referrals-analytics',
  sub_dashboard: 'overview',
  table: 'referrals_received-data-table',
} as const;

export const programsOverviewTableColumnsQuery = {
  product: 'referrals',
  dashboard: 'referrals-analytics',
  sub_dashboard: 'programs-view',
  table: 'program-overview-data-table',
} as const;

export const jobsOverviewTableColumnsQuery = {
  product: 'referrals',
  dashboard: 'referrals-analytics',
  sub_dashboard: 'jobs-view',
  table: 'jobs-overview-data-table',
} as const;

export const payoutsOverviewTableColumnsQuery = {
  product: 'referrals',
  dashboard: 'referrals-analytics',
  sub_dashboard: 'payouts-view',
  table: 'payout-details-data-table',
} as const;

export const getMenuOptionsForReferralsOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'referrer_email', label: t('Referrer_Email', 'Referrer Email')},
  {
    key: 'total_referrals_received',
    label: t('Total_Referrals_Received', 'Total Referrals Received'),
  },
  {
    key: 'total_referrals_hired',
    label: t('Total_Referrals_Hired', 'Total Referrals Hired'),
  },
];

export const getMenuOptionsForProgramsOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'program_owner', label: t('Channel', 'Channel')},
  {
    key: 'candidate_referred',
    label: t('Candidates_Referred', 'Candidates Referred'),
  },
  {key: 'candidate_hired', label: t('Candidates_Hired', 'Candidates Hired')},
  {key: 'conversion_rate', label: t('Conversion_Rate', 'Conversion Rate')},
  {key: 'avg_time_to_fill', label: t('Avg_Time_to_Fill', 'Avg Time to Fill')},
];

export const getMenuOptionsForJobsOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'avg_time_to_fill', label: t('Avg_Time_to_Fill', 'Avg Time to Fill')},
  {key: 'candidate_hired', label: t('Candidates_Hired', 'Candidates Hired')},
  {
    key: 'candidate_referred',
    label: t('Candidates_Referred', 'Candidates Referred'),
  },
  {key: 'conversion_rate', label: t('Conversion_Rate', 'Conversion Rate')},
  {key: 'job_owner', label: t('Job_Owner', 'Job Owner')},
  {key: 'program_name', label: t('Program_Name', 'Program Name')},
  {key: 'referral_source', label: t('Referral_Source', 'Referral Source')},
];

export const getMenuOptionsForPayoutsOverviewTable = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'total_referrals_received',
    label: t('Total_Referrals_Received', 'Total Referrals Received'),
  },
  {
    key: 'total_referrals_hired',
    label: t('Total_Referrals_Hired', 'Total Referrals Hired'),
  },
  {key: 'payout_pending', label: t('Payout_Pending', 'Payout Pending')},
  {key: 'payout_completed', label: t('Payout_Completed', 'Payout Completed')},
  {key: 'total_payout', label: t('Total_Payout', 'Total Payout')},
];

export const ReferralOverviewKpiMetricsOrder = [
  'candidate_referred',
  'net_new_candidate_sourced',
  'candidates_hired',
  'avg_time_to_fill',
  'candidate_referred_without_any_job',
] as const;

export const ReferralTimeToHireMetricsOrder = [
  'referrals',
  'without_referrals',
] as const;

export const ReferralPayoutMetricsOrder = [
  'total_payouts',
  'payouts_completed',
  'payouts_pending',
] as const;

export const getAnalyticsReferralsTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: string;
  label: string;
}> => [
  {
    id: 'overview',
    label: t('Overview', 'Overview'),
  },
  {
    id: 'programs-and-jobs',
    label: t('Programs_Jobs', 'Programs & Jobs'),
  },
  {
    id: 'payouts',
    label: t('Payouts', 'Payouts'),
  },
];
export const getModuleTypeLabel = (
  moduleType: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (moduleType) {
    case 'multiple_choice_survey_question':
      return t('Multiple_Choice', 'Multiple Choice');
    case 'rating_scale_survey_question':
      return t('Rating_Scale', 'Rating Scale');
    case 'calendar_date_survey_question':
      return t('Calendar_Date', 'Calendar Date');
    case 'nps_survey_question':
      return t('NPS', 'NPS');
    case 'beefree_module':
      return t('Beefree', 'Beefree');
    case 'attribute_list_module':
      return t('Attribute_List', 'Attribute List');
    case 'sms_message_survey_module':
      return t('SMS_Message', 'SMS Message');
    case 'message_survey_module':
      return t('Message', 'Message');
    default:
      return moduleType;
  }
};
export const getHiringPipelineTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: string;
  label?: string;
}> => [
  {
    id: 'pipeline-velocity',
    label: t('Pipeline_and_Velocity', 'Pipeline and Velocity'),
  },
  {id: 'job-status-age', label: t('Job_Status_Age', 'Job Status & Age')},
  {
    id: 'interview-schedule',
    label: t('Interview_Schedules_Feedback', 'Interview Schedules & Feedback'),
  },
];

export const HIRING_PIPELINE_TABS_OBJECT = Object.freeze({
  'pipeline-velocity': 'pipeline-velocity',
  job_status_age: 'job_status-age',
  interview_schedule: 'interview-schedule',
});

export const GetPipelineVelocityDrilldownMetricTopValue = (
  key: string,
): string => {
  const metricLabels = {
    total_applications: 'Total Applications',
    shortlisted: 'Shortlisted',
    interviewed: 'Interviewed',
    offered: 'Offered',
    joined: 'Joined',
    rejected: 'Rejected',
  } as const;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly total_applications: "Total Applications"; readonly shortlisted: "Shortlisted"; readonly interviewed: "Interviewed"; readonly offered: "Offered"; readonly joined: "Joined"; readonly rejected: "Rejected"; }'.
  return metricLabels[key] || key;
};

export const transformTreeOptionsWithCompositeKeys = (
  nodes: Array<TreeNode>,
  parentPath: string = '',
): Array<TreeNode> => {
  return nodes.map((node) => {
    // Encode the node key
    const encodedKey = encodeURIComponent(node.key);

    // Create the composite key (parent/child format)
    const compositeKey = parentPath
      ? `${parentPath}/${encodedKey}`
      : encodedKey;

    // Recursively transform children, passing compositeKey as the new parentPath
    const transformedChildren = node.children
      ? transformTreeOptionsWithCompositeKeys(node.children, compositeKey)
      : [];

    return {
      ...node,
      key: compositeKey, // Replace the original key with the composite key
      children: transformedChildren,
    };
  });
};
// Helper to convert a flat list of composite keys back into a tree structure of TreeNodeKey.
// Each composite key is split on '/' (which was used as the joiner) and decoded.
// The resulting tree merges common prefix paths.
export const flatSelectedKeysToTreeNodeKeys = (
  selectedKeys: Array<string>,
): Array<TreeNodeKey> => {
  const tree: Array<TreeNodeKey> = [];
  for (const compositeKey of selectedKeys) {
    const parts = compositeKey.split('/').map(decodeURIComponent);
    let currentLevel = tree;
    for (const part of parts) {
      let node = currentLevel.find((n) => n.key === part);
      if (!node) {
        node = {key: part, children: []};
        currentLevel.push(node);
      }
      currentLevel = node.children;
    }
  }
  return tree;
};

// New helper: Iterative version to convert a tree of TreeNodeKey back into a flat list
// of composite keys. Each composite key is computed by encoding each segment of the path
// (from the root) and joining them with '/'.
export const treeNodeKeysToFlatSelectedKeys = (
  tree: Array<TreeNodeKey>,
): Array<string> => {
  const result: Array<string> = [];
  const stack: Array<{
    node: TreeNodeKey;
    path: Array<string>;
  }> = [];

  // Initialize the stack with all root-level nodes.
  for (const root of tree) {
    stack.push({node: root, path: []});
  }

  // Process nodes iteratively.
  while (stack.length > 0) {
    // @ts-expect-error - TS2339 - Property 'node' does not exist on type '{ node: TreeNodeKey; path: string[]; } | undefined'. | TS2339 - Property 'path' does not exist on type '{ node: TreeNodeKey; path: string[]; } | undefined'.
    const {node, path} = stack.pop();
    const newPath = [...path, node.key];
    // Compute composite key by URL-encoding each segment and joining them with '/'
    const compositeKey = newPath.map(encodeURIComponent).join('/');
    result.push(compositeKey);
    if (node.children && node.children.length > 0) {
      // Push children into the stack (in reverse order if order matters)
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push({node: node.children[i], path: newPath});
      }
    }
  }

  return result;
};

export const transformTreeOptions = (
  trees: Array<Node> = [],
): Array<TreeNode> => {
  const result: Array<TreeNode> = [];

  // Process each root-level tree node.
  trees.forEach((tree: Node) => {
    const newTree: TreeNode = {
      key: tree.key,
      label: tree.value,
      children: [],
    };
    result.push(newTree);

    // The stack holds pairs { source, target } to process each node iteratively.
    const stack: Array<{
      source: Node;
      target: TreeNode;
    }> = [{source: tree, target: newTree}];

    // Iteratively traverse the tree.
    while (stack.length > 0) {
      // @ts-expect-error - TS2339 - Property 'source' does not exist on type '{ source: Node; target: TreeNode; } | undefined'. | TS2339 - Property 'target' does not exist on type '{ source: Node; target: TreeNode; } | undefined'.
      const {source, target} = stack.pop();

      if (Array.isArray(source.children) && source.children.length > 0) {
        // Map the source children to transformed children.
        target.children = source.children.map((child: Node) => ({
          key: child.key,
          label: child.value,
          children: [],
        }));

        // Push each pair onto the stack for further processing.
        for (let i = 0; i < source.children.length; i++) {
          stack.push({source: source.children[i], target: target.children[i]});
        }
      }
    }
  });

  return result;
};

export const compressSelectedKeys = (
  selectedKeys: Array<string>,
  treeNodes: Array<Node>,
): Array<string> => {
  const result = new Set(selectedKeys);

  // Helper function to recursively delete all descendant keys of a node.
  const deleteDescendants = (node: Node) => {
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        result.delete(child.key);
        deleteDescendants(child);
      });
    }
  };

  // Traverse the tree: if the current node is selected, remove all its descendant keys immediately.
  const traverseAndRemove = (node: Node) => {
    if (result.has(node.key)) {
      deleteDescendants(node);
    } else if (node.children && node.children.length > 0) {
      node.children.forEach(traverseAndRemove);
    }
  };

  treeNodes.forEach(traverseAndRemove);
  const compressed = Array.from(result);
  return compressed;
};

// Function to encode and create the composite key
const createCompositeKey = (source: string, subSource: string): string => {
  // Encode the parts of the key to handle special characters like slashes
  return `${encodeURIComponent(source)}/${encodeURIComponent(subSource)}`;
};

export const transformDataToKeys = (
  data: Array<JobSource>,
  // The original tree structure
  jobSourceOptions: Array<TreeNode>,
): Array<string> => {
  const selectedKeys = new Set();
  const sourceToSubSources = new Map();

  // Step 1: Extract selected keys and group by source
  data.forEach((item) => {
    const {source, sub_source} = item;
    if (!sourceToSubSources.has(source)) {
      sourceToSubSources.set(source, new Set());
    }
    sub_source.forEach((subSource) => {
      const compositeKey = createCompositeKey(source, subSource);
      selectedKeys.add(compositeKey);
      sourceToSubSources.get(source)?.add(subSource);
    });
  });

  // Step 2: If all sub-sources of a source are selected, also add the source key
  jobSourceOptions.forEach(({key: source, children}) => {
    if (children.length > 0) {
      const totalSubSources = new Set(children.map((child) => child.key));
      const selectedSubSources = sourceToSubSources.get(source) || new Set();

      if (totalSubSources.size === selectedSubSources.size) {
        // Add the source key if all sub-sources are selected
        selectedKeys.add(encodeURIComponent(source));
      }
    }
  });

  // @ts-expect-error - TS2322 - Type 'unknown[]' is not assignable to type 'string[]'.
  return Array.from(selectedKeys);
};

export const convertKeysToJobSource = (
  selectedKeys: Array<string>,
): Array<JobSource> => {
  const jobSourceMap: Record<string, any> = {};

  selectedKeys.forEach((key) => {
    // Split into exactly two parts: [source, subSource]
    const [rawSource, rawSubSource] = key.split('/');

    // Decode components
    const source = decodeURIComponent(rawSource);
    const subSource = rawSubSource ? decodeURIComponent(rawSubSource) : null;

    // Ensure the source exists in the map
    if (!jobSourceMap[source]) {
      jobSourceMap[source] = {
        source,
        sub_source: [],
      };
    }

    // Add sub-source only if it exists
    if (subSource !== null) {
      jobSourceMap[source].sub_source.push(subSource);
    }
  });

  // Convert the map to an array and return
  return Object.keys(jobSourceMap).map((key) => jobSourceMap[key]);
};

export const compressJobSource = (
  selectedJobSources: Array<JobSource>,
  options: Array<TreeNode>,
): Array<JobSource> => {
  // Create a map of sources to their total sub-sources count
  const totalSubSourcesMap: Record<string, any> = {};
  options.forEach((source) => {
    if (source.children) {
      totalSubSourcesMap[source.key] = source.children.length;
    }
  });

  return selectedJobSources.map((jobSource: JobSource) => {
    const source = jobSource.source;
    if (totalSubSourcesMap[source] === jobSource.sub_source.length) {
      return {
        source,
        sub_source: [],
      };
    }
    return jobSource;
  });
};

export const getCustomExportOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  format: string;
  key: string;
  label: string;
}> => [
  {
    format: 'image/jpeg',
    key: 'download_as_jpeg',
    label: t('Download_as_JPEG', 'Download as JPEG'),
  },
  {
    format: 'image/png',
    key: 'download_as_png',
    label: t('Download_as_PNG', 'Download as PNG'),
  },
  {
    format: 'application/pdf',
    key: 'download_as_pdf',
    label: t('Download_as_PDF', 'Download as PDF'),
  },
];

export const getFunnelTypeOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'milestone', label: t('Milestone', 'Milestone')},
  {key: 'stage', label: t('Stage', 'Stage')},
];

export const getIndeterminateNodes = (
  node: TreeNode,
  selectedKeys: Set<string>,
): Set<string> => {
  if (!node.children || node.children.length === 0) {
    return new Set();
  }

  const selectedChildren = node.children.filter((child) =>
    selectedKeys.has(child.key),
  );
  const childIndeterminateValues = new Set();

  node.children.forEach((child) => {
    const childIndeterminate = getIndeterminateNodes(child, selectedKeys);
    childIndeterminate.forEach((value) => childIndeterminateValues.add(value));
  });

  if (
    selectedChildren.length > 0 &&
    selectedChildren.length < node.children.length
  ) {
    childIndeterminateValues.add(node.key);
  }

  // @ts-expect-error - TS2322 - Type 'Set<unknown>' is not assignable to type 'Set<string>'.
  return childIndeterminateValues;
};

export const initializeTreeState = (
  treeOptions: Array<TreeNode>,
  selectedKeys: Array<string>,
): MultilevelFilterState => {
  const selectedKeysSet = new Set(selectedKeys);
  const indeterminateValues = getIndeterminateNodes(
    {key: '__root__', children: treeOptions},
    selectedKeysSet,
  );

  return {
    currentNode: {
      key: '__root__',
      label: 'Root',
      children: treeOptions,
    },
    history: [],
    indeterminateValues: Array.from(indeterminateValues),
  };
};

export const defaultTreeState: MultilevelFilterState = {
  currentNode: {key: '__root__', label: 'Root', children: []},
  history: [],
  indeterminateValues: [],
};

export const findNodeByKey = (
  root: TreeNode,
  key: string,
): TreeNode | null | undefined => {
  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
    if (node.key === key) {
      return node;
    }
    // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
    if (node.children) {
      // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
      stack.push(...node.children);
    }
  }
  return null;
};

export const selectDescendants = (
  node: TreeNode,
  selectedSet: Set<string>,
): void => {
  if (!node.children) {
    return;
  }
  node.children.forEach((child) => {
    selectedSet.add(child.key);
    selectDescendants(child, selectedSet);
  });
};

export const deselectDescendants = (
  node: TreeNode,
  selectedSet: Set<string>,
): void => {
  if (!node.children) {
    return;
  }
  node.children.forEach((child) => {
    selectedSet.delete(child.key);
    deselectDescendants(child, selectedSet);
  });
};

export const getMultilevelPlaceholderText = (
  options: Array<TreeNode>,
  selectedKeys: Array<string>,
  title: string,
  t?: (arg1: string, arg2: string) => string,
): string => {
  const allPossibleKeys: string[] = [];
  const selectedLabels: string[] = [];

  const toVisit = [...options];

  while (toVisit.length > 0) {
    const node = toVisit.pop();

    // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
    allPossibleKeys.push(node.key);

    // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
    if (selectedKeys.includes(node.key)) {
      // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
      selectedLabels.push(node.label || '');
    }

    // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
    if (node.children) {
      // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
      toVisit.push(...node.children);
    }
  }

  if (
    allPossibleKeys.length > 0 &&
    allPossibleKeys.every((key) => selectedKeys.includes(key))
  ) {
    return `${t ? t('All', 'All') : 'All'} ${title}`;
  }

  return selectedLabels.length > 0
    ? selectedLabels.join(', ')
    : `${t ? t('Select', 'Select') : 'Select'} ${title}`;
};

export const hoursToDays = (
  hours: number,
  t: (arg1: string, arg2: string) => string,
): string => {
  const hoursInADay = 24;

  if (hours === 0) {
    return '0';
  }

  if (hours < hoursInADay) {
    return `<1 ${t('day', 'day')}`;
  }

  // Convert to days
  const days = Math.round(hours / hoursInADay);
  return `${days} ${t('days', 'days')}`;
};

export const EXPORT_CRM_ALL_APPLICANTS = 'data-exports/crm/all-applicants';

export const getAllWorkflows = (
  t: (arg1: string, arg2: string) => string,
): {
  id: string;
  name: string;
} =>
  Object.freeze({
    id: 'all_workflows',
    name: t('All_Workflows', 'All Workflows'),
  });
export const getAllJourneys = (
  t: (arg1: string, arg2: string) => string,
): {
  id: string;
  name: string;
} =>
  Object.freeze({
    id: 'all_journeys',
    name: t('All_Journeys', 'All Journeys'),
  });

export const getDatesDiffInDays = (
  startDate?: string | null,
  endDate?: string | null,
): number => {
  if (!startDate || !endDate) {
    return 0;
  }
  return moment(endDate).diff(moment(startDate), 'days');
};
/**
 * Determines if line chart data is effectively empty (all zero values or no data)
 *
 * @param {Array<LineSeriesItem>} data - The chart data to check
 * @return {boolean} - True if the chart has no meaningful data to display
 */
export const isLineChartDataEmpty = (data: Array<LineSeriesItem>): boolean => {
  if (!data) {
    return true;
  }

  if (!Array.isArray(data)) {
    return true;
  }

  return data.every(
    (series) =>
      Array.isArray(series.data) && series.data.every((point) => point.y === 0),
  );
};

/**
 * Determines if sankey chart data is effectively empty (all zero values or no data)
 *
 * @param {Record<string, number>} data - The sankey chart data object to check
 * @return {boolean} - True if the chart has no meaningful data to display
 */
export const isSankeyChartDataEmpty = (
  data: Record<string, number> | undefined,
): boolean => {
  if (!data || typeof data !== 'object') {
    return true;
  }

  const values = Object.values(data);
  if (values.length === 0) {
    return true;
  }

  return values.every((value) => value === 0);
};

/**
 * Determines if funnel chart data is effectively empty (all zero values or no data)
 *
 * @param {Array<FunnelSeriesItem>} data - The chart data to check
 * @return {boolean} - True if the chart has no meaningful data to display
 */
export const isFunnelChartDataEmpty = (
  data: Array<FunnelSeriesItem>,
): boolean => {
  if (!Array.isArray(data)) {
    return true;
  }

  return data.every(
    (series) =>
      Array.isArray(series.data) &&
      series.data.every(
        (point) => typeof point.y === 'number' && point.y === 0,
      ),
  );
};

/**
 * Determines if Column chart data is effectively empty (all zero values or no data)
 *
 * @param {Array<ColumnSeries>} data - The chart data to check
 * @return {boolean} - True if the chart has no meaningful data to display
 */
export const isColumnChartDataEmpty = (data?: Array<ColumnSeries>): boolean => {
  if (!Array.isArray(data)) {
    return true;
  }

  return data.every(
    (series) =>
      Array.isArray(series.data) &&
      series.data.every(
        (point) => typeof point.y === 'number' && point.y === 0,
      ),
  );
};

/**
 * Determines if Donut chart data is effectively empty (all zero values or no data)
 *
 * @param {Array<SeriesOptionsType>} data - The chart data to check
 * @return {boolean} - True if the chart has no meaningful data to display
 */
export const isDonutChartDataEmpty = (
  data?: Array<PieSeriesOptions>,
): boolean => {
  if (!Array.isArray(data)) {
    return true;
  }

  return data.every(
    (series) =>
      Array.isArray(series.data) &&
      series.data.every(
        (point) =>
          typeof (point as any)?.y === 'number' && (point as any)?.y === 0,
      ),
  );
};

export const getCurrencySettingsOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'USD',
    label: `${t('United_States_Dollar', 'United States Dollar')} - ${t(
      'USD',
      'USD',
    )}`,
  },
  {
    key: 'INR',
    label: `${t('Indian_Rupee', 'Indian Rupee')} - ${t('INR', 'INR')}`,
  },
];

export const getSourcePerformanceMetricSourceOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'candidate_added_by_source',
    label: t('Candidate_Added_by_Source', 'Candidate Added by Source'),
  },
  {
    key: 'candidate_offered_by_source',
    label: t('Candidate_Offered_by_Source', 'Candidate Offered by Source'),
  },
  {
    key: 'candidate_hired_by_source',
    label: t('Candidate_Hired_by_Source', 'Candidate Hired by Source'),
  },
  {
    key: 'cost_per_source',
    label: t('Cost_Per_Source', 'Cost Per Source'),
  },
];

export const getAnalyticsCandidateSourcePerformanceTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: string;
  label: string;
}> => [
  {
    id: 'all-sources',
    label: t('All_Sources', 'All Sources'),
  },
  // Removing for now as APIs are not availableF
  // {
  //   id: 'agency-performance',
  //   label: 'Agency Performance',
  // },
];

export const convertToTitleCase = (source?: string | null): string => {
  if (!source) {
    return '';
  }

  return source
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
export const getAllSurveysExportOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: '1', label: t('Download_this_table', 'Download this table')},
  {key: '2', label: t('Download_MCQ_summary', 'Download MCQ summary')},
];

export const SURVEY_ANSWER_MAX_DISPLAY_LENGTH = 125;

export const getMilestoneName = (key: string): string | undefined => {
  let milestone_name;
  if (key === 'candidates_offered') {
    milestone_name = 'Offer';
  }

  if (key === 'candidates_hired') {
    milestone_name = 'Hire';
  }
  return milestone_name;
};

export const getRecipientResource = (
  recipient_resource: string,
  t: TranslationFunction,
): string => {
  if (recipient_resource === 'client') {
    return t('Client', 'Client');
  } else if (recipient_resource === 'audience_member') {
    return t('Candidate', 'Candidate');
  } else if (recipient_resource === 'internal') {
    return t('Internal_Employee', 'Internal Employee');
  }

  return t('NA', 'N/A');
};

export const DEFAULT_SERIES_NAMES = [
  'Sent',
  'Delivered',
  'Delivery Rate',
  'Bounced',
  'Bounce Rate',
  'Replies',
  'Reply Rate',
] as const;

export const QUESTION_TYPE_FILTER_ENABLED_TABS = [
  'all-responses',
  'all-surveys',
  'flagged-responses',
] as const;

export const getProductNameMapping = (
  productName: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (productName) {
    case 'Performance':
      return t('Performance', 'Performance');
    case 'Journey':
      return t('Journey', 'Journey');
    case 'Workflow':
      return t('Workflow', 'Workflow');
    case 'Survey and NPS':
      return t('Survey_and_NPS', 'Survey and NPS');
    case 'Chatbot':
      return t('Chatbot', 'Chatbot');
    case 'Referrals':
      return t('Referrals', 'Referrals');
    case 'Other':
      return t('Other', 'Other');
    case 'CRM':
      return t('CRM', 'CRM');
    default:
      return productName;
  }
};

export const getSubProductNameMapping = (
  subProductName: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  switch (subProductName) {
    case 'Performance':
      return t('Performance', 'Performance');
    case 'Talent Engagement ROI':
      return t('Talent_Engagement_ROI', 'Talent Engagement ROI');
    case 'Overview':
      return t('Overview', 'Overview');
    case 'Delivery History':
      return t('Delivery_History', 'Delivery History');
    case 'External Link Clicks (Old)':
      return `${t('External_Link_Clicks', 'External Link Clicks')}(${t(
        'Old',
        'Old',
      )})`;
    case 'Engagement and Deliverability':
      return t(
        'Engagement_and_Deliverability',
        'Engagement and Deliverability',
      );
    case 'External Link Clicks':
      return t('External_Link_Clicks', 'External Link Clicks');
    case 'Responses':
      return t('Responses', 'Responses');
    case 'Flagged Responses':
      return t('Flagged_Responses', 'Flagged Responses');
    case 'NPS Overview':
      return t('NPS_Overview', 'NPS Overview');
    case 'Surveys and NPS':
      return t('Surveys_and_NPS', 'Surveys and NPS');
    case 'Chatbot Overview':
      return t('Chatbot_Overview', 'Chatbot Overview');
    case 'Chatbot Transcripts':
      return t('Chatbot_Transcripts', 'Chatbot Transcripts');
    case 'Voicebot Transcripts':
      return t('Voicebot_Transcripts', 'Voicebot Transcripts');
    case 'Referrals Analytics':
      return t('Referrals_Analytics', 'Referrals Analytics');
    case 'Meetings Overview':
      return t('Meetings_Overview', 'Meetings Overview');
    case 'Messaging Overview':
      return t('Messaging_Overview', 'Messaging Overview');
    case 'Job Matching Metrics':
      return t('Job_Matching_Metrics', 'Job Matching Metrics');
    case 'Audit':
      return t('Audit', 'Audit');
    case 'Timeline':
      return t('Timeline', 'Timeline');
    case 'Recruiter Productivity':
      return t('Recruiter_Productivity', 'Recruiter Productivity');
    case 'Candidate Source Performance':
      return t('Candidate_Source_Performance', 'Candidate Source Performance');
    case 'Milestone Analytics':
      return t('Milestone_Analytics', 'Milestone Analytics');
    case 'User Activity':
      return t('User_Activity', 'User Activity');
    case 'Source Analytics':
      return t('Source_Analytics', 'Source Analytics');
    case 'Job Status':
      return t('Job_Status', 'Job Status');
    case 'Job Age':
      return t('Job_Age', 'Job Age');
    case 'Hiring Pattern':
      return t('Hiring_Pattern', 'Hiring Pattern');
    case 'Candidate Pipeline By Job':
      return t('Candidate_Pipeline_By_Job', 'Candidate Pipeline By Job');
    case 'Agency Report':
      return t('Agency_Report', 'Agency Report');
    case 'Candidate Wise':
      return t('Candidate_Wise', 'Candidate Wise');
    case 'Recruiter Quality':
      return t('Recruiter_Quality', 'Recruiter Quality');
    case 'Candidate Experience Survey':
      return t('Candidate_Experience_Survey', 'Candidate Experience Survey');
    case 'Time To Hire By Job':
      return t('Time_To_Hire_By_Job', 'Time To Hire By Job');
    case 'Interview':
      return t('Interview', 'Interview');
    case 'Offer':
      return t('Offer', 'Offer');
    case 'Onboarding':
      return t('Onboarding', 'Onboarding');
    case 'Hiring Pipeline':
      return t('Hiring_Pipeline', 'Hiring Pipeline');
    case 'All Applicants':
      return t('All_Applicants', 'All Applicants');
    default:
      return subProductName;
  }
};

export const getAllJourneyTableExportOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: '1', label: t('Download_This_Table', 'Download This Table')},
  {key: '2', label: t('Download_All_Touchpoints', 'Download All Touchpoints')},
];

export const getAllWorkflowTableExportOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'downloadThisTable',
    label: t('Download_This_Table', 'Download This Table'),
  },
  {
    key: 'downloadAllNodes',
    label: t('Download_All_Nodes', 'Download All Nodes'),
  },
  {
    key: 'downloadAllRecipients',
    label: t('Download_All_Recipients', 'Download All Recipients'),
  },
];

// Voice Call Analytics Column Query Functions
export const voiceCallTableColumnsQuery = {
  product: 'voice-call-analytics',
  dashboard: 'voice-call-analytics',
  sub_dashboard: 'all-call-records',
  table: 'call-records-table',
};

export const voiceflowTableColumnsQuery = {
  product: 'voice-call-analytics',
  dashboard: 'voice-call-analytics',
  sub_dashboard: 'all-voiceflows',
  table: 'voiceflows-table',
};

export const voiceCallWorkflowTableColumnsQuery = {
  product: 'voice-call-analytics',
  dashboard: 'voice-call-analytics',
  sub_dashboard: 'all-workflows',
  table: 'workflows-table',
};

export const voiceCallChartColumnsQuery = {
  product: 'voice-call-analytics',
  dashboard: 'voice-call-analytics',
  sub_dashboard: 'overview',
  chart_type: 'conversion_trend',
};

export const voiceCallDurationChartColumnsQuery = {
  product: 'voice-call-analytics',
  dashboard: 'voice-call-analytics',
  sub_dashboard: 'overview',
  chart_type: 'duration_distribution',
};

export const voiceCallDroppedReasonsChartColumnsQuery = {
  product: 'voice-call-analytics',
  dashboard: 'voice-call-analytics',
  sub_dashboard: 'overview',
  chart_type: 'dropped_reasons',
};
export enum DashboardPermissionEnum {
  Onboarding = 'Onboarding',
  AgencyReport = 'Agency Report',
  CandidateExperienceSurvey = 'Candidate Experience Survey',
  TimeToHireByJob = 'Time To Hire By Job',
  Offer = 'Offer',
  Interview = 'Interview',
  RecruiterQuality = 'Recruiter Quality',
  SourceAnalytics = 'Source Analytics',
  JobAge = 'Job Age',
  CandidateWise = 'Candidate Wise',
  JobStatus = 'Job Status',
  CustomReports = 'Custom Reports',
  UserActivity = 'User Activity',
  HiringPattern = 'Hiring Pattern',
  CandidatePipelineByJob = 'Candidate Pipeline By Job',
  MilestoneAnalytics = 'Milestone Analytics',
}

export const getConversionChartTypeOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'shanky_view',
    label: t('Sankey', 'Sankey'),
    iconLeft: 'diagram-sankey',
  },
  {key: 'funnel_view', label: t('Funnel', 'Funnel'), iconLeft: 'filter'},
];

export const getCompletedCallOutcomesChartTypeOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'trend_view', label: t('Trend_View', 'Trend View')},
  {key: 'column_view', label: t('Column_Chart', 'Column Chart')},
];

export const getDroppedCallReasonsChartTypeOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'pie_view',
    label: t('Distribution', 'Distribution'),
    iconLeft: 'chart-pie',
    iconLeftType: 'solid',
  },
  {
    key: 'trend_view',
    label: t('Trend_View', 'Trend View'),
    iconLeft: 'chart-line-up',
  },
];

export const getCallConversionMetricOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'absolute', label: t('Absolute_Values', 'Absolute Values')},
  {key: 'percentage', label: t('Conversion_Rates', 'Conversion Rates')},
];

export const getVoiceCallStatusKey = (key: string): Array<string> => {
  const CALL_STATUS_KEYS_MAP: Record<string, string[]> = {
    calls_attempted: [
      'started',
      'complete',
      'terminated',
      'terminated_rescheduled',
      'no_answer',
    ],
    calls_answered: [
      'started',
      'complete',
      'terminated',
      'terminated_rescheduled',
    ],
    calls_dropped: ['terminated'],
    calls_completed: ['complete'],
    calls_rescheduled: ['terminated_rescheduled'],
  };
  return CALL_STATUS_KEYS_MAP[key] || [];
};

export const STATUS_INDICATOR_MAP: Record<string, StatusSemanticType> = {
  complete: 'success',
  queued: 'information',
  attempted: 'information',
  started: 'information',
  terminated_rescheduled: 'information',
  no_answer: 'danger',
  terminated: 'danger',
};

export const getStatusIndicator = (status: string): StatusSemanticType => {
  return STATUS_INDICATOR_MAP[status] || 'information';
};

export const toSnakeCase = (str: string): string => {
  return str.replace(/\s+/g, '_').toLowerCase();
};

export const getExportOptionsForWorkflowActivity = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {key: '1', label: t('Filtered_Date_Range', 'Filtered Date Range')},
  {key: '2', label: t('Overall_Date_Range', 'Overall Date Range')},
];

// Scheduler Panel Utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const generateMonthDayOptions = (
  t: TranslationFunction,
): Array<MenuOption> => {
  const days: Array<MenuOption> = [];
  for (let day = 1; day <= 28; day++) {
    days.push({
      key: day.toString(),
      label: `${day}`,
    });
  }
  days.push({
    key: 'last',
    label: t('Last_day_of_month', 'Last day of month'),
  });
  return days;
};

export const generateTimeOptions = (): Array<MenuOption> => {
  const times: Array<MenuOption> = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
      times.push({key: timeString, label: timeString});
    }
  }
  return times;
};

// Scheduler Panel Option Generators
export const getSchedularPanelFrequencyOptions = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {key: 'daily', label: t('Daily', 'Daily')},
  {key: 'weekly', label: t('Every_week', 'Every week')},
  {key: 'monthly', label: t('Every_month', 'Every month')},
];

export const getSchedularPanelDayOptions = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {key: 'sunday', label: t('Sunday', 'Sunday')},
  {key: 'monday', label: t('Monday', 'Monday')},
  {key: 'tuesday', label: t('Tuesday', 'Tuesday')},
  {key: 'wednesday', label: t('Wednesday', 'Wednesday')},
  {key: 'thursday', label: t('Thursday', 'Thursday')},
  {key: 'friday', label: t('Friday', 'Friday')},
  {key: 'saturday', label: t('Saturday', 'Saturday')},
];

// Day of Week Mapping Functions
export const mapDayOfWeekToString = (
  dayOfWeek: number | null,
): string | undefined => {
  if (dayOfWeek === null) {
    return undefined;
  }
  const dayMap: Record<number, string> = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };
  return dayMap[dayOfWeek];
};

export const mapDayOfWeek = (day: string): number | null => {
  const dayMap: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  return dayMap[day] ?? null;
};
